import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AirplaneLoader from "./AirplaneLoader";

const formatTime = (iso) => { if (!iso) return "N/A"; return iso.split("T")[1]?.substring(0, 5) || iso; };
const formatDur = (val) => { if (!val) return "N/A"; if (typeof val === "string") return val; return `${Math.floor(val / 60)}h ${val % 60}m`; };
const depHour = (dep) => parseInt((dep || "00").split(":")[0]);
const timeSlot = (dep) => { const h = depHour(dep); if (h < 6) return 1; if (h < 12) return 2; if (h < 18) return 3; return 4; };
const formatDateShort = (dateStr) => {
  if (!dateStr) return "";
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return "";
  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${days[dt.getDay()]}, ${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
};

// ── Duration helpers (sorting ke liye) ──
// "2h 20m", "2h", "45m", "02:20", "02:20:00" ya number -> minutes
const durationToMinutes = (val) => {
  if (val === null || val === undefined || val === "") return Infinity;
  if (typeof val === "number") return val;
  const str = String(val).trim();

  if (/[hm]/i.test(str)) {
    const h = str.match(/(\d+)\s*h/i);
    const m = str.match(/(\d+)\s*m/i);
    return (h ? parseInt(h[1]) * 60 : 0) + (m ? parseInt(m[1]) : 0);
  }
  const parts = str.split(":").map(Number);
  if (parts.length >= 2 && parts.every((n) => !isNaN(n))) return parts[0] * 60 + parts[1];

  const n = Number(str);
  return isNaN(n) ? Infinity : n;
};

const formatMins = (mins) =>
  isFinite(mins) ? `${Math.floor(mins / 60)}h ${mins % 60}m` : "N/A";

// Infinity - Infinity = NaN se bachne ke liye
const cmpNum = (a, b) => (a === b ? 0 : a - b);

const diffMinutes = (startIso, endIso) => {
  if (!startIso || !endIso) return null;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  const mins = Math.round((end.getTime() - start.getTime()) / 60000);
  return mins >= 0 ? mins : null;
};
const formatLayover = (mins) => {
  if (mins === null || mins === undefined) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
};

// TDS ka fallback %. Backend se TDS/NetFare aaye to wahi use hota hai.
const TDS_RATE = 5;

const AIRLINE_LOGO_MAP = {};
const CLASS_NAMES = {
  0: "Economy",
  1: "Premium Economy",
  2: "Business",
  3: "First Class",
};
const getLogoFileName = (code) => AIRLINE_LOGO_MAP[code] || code || "default";

// ── Domestic / International detect ──
// Pehle airports API ka country field dekhta hai. Wo na mile to ye fallback list use hoti hai,
// taaki one way / round trip dono me international sahi pakdi jaye.
const INDIAN_AIRPORT_CODES = new Set([
  "DEL","BOM","BLR","MAA","CCU","HYD","AMD","PNQ","GOI","COK","JAI","LKO","ATQ","IXC","GAU","PAT","BBI","IXR","NAG",
  "IDR","BHO","RPR","VNS","SXR","IXJ","IXB","IMF","AGR","UDR","JDH","IXM","TRV","CJB","IXE","VGA","VTZ","RAJ","STV",
  "HBX","IXG","IXL","DED","IXZ","DIB","JRH","BDQ","TIR","IXA","AJL","DIU","KNU","CCJ","GOX","HSR","IXD","KLH","IXU",
  "NMI","PBD","BHJ","AGX","HJR","ISK","TRZ","TCR","IXS","JLR","GWL","BKB","JGB","IXH","IXI","IXK","IXN","IXP","IXT",
  "IXV","IXY","JSA","KQH","LUH","MYQ","PGH","PNY","SHL","SLV","TEZ","TEI","VDY","ZER","RGH","RRK","JGA","CDP","DBR",
]);

const isIndianAirportCode = (code, airports) => {
  if (!code) return null;
  const a = (airports || []).find((x) => x.AirportCode === code);
  const cc = a && (a.CountryCode || a.CountryName || a.Country); // field naam alag ho to yahan badal lena
  if (cc) {
    const v = String(cc).trim().toUpperCase();
    return v === "IN" || v === "INDIA";
  }
  return INDIAN_AIRPORT_CODES.has(String(code).toUpperCase());
};

// ── Fare resolver: API alag-alag shape me fare de sakti hai (object / array / contract level) ──
const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const firstPositive = (...vals) => { for (const v of vals) { const n = toNum(v); if (n > 0) return n; } return 0; };

const resolveFare = (flight) => {
  const f = flight?.AirlineFare ?? flight?.Fare ?? flight?.FareDetails ?? flight?.AirFare ?? {};
  if (Array.isArray(f)) {
    // Passenger-type wise fare aaye to sab ko jod do
    const keys = ["GrossFare", "BaseFare", "TaxFare", "Tax", "NetFare", "TDS", "TDSAmount", "TotalFare"];
    const sum = {};
    keys.forEach((k) => { sum[k] = f.reduce((a, x) => a + toNum(x?.[k]), 0); });
    return sum;
  }
  return f || {};
};

let __priceWarned = false;

const getDisplayData = (flight) => {
  const segs = flight?.AirSegments || [];

  // API me spelling "Onword" hai (typo), isliye dono handle kar rahe hain
  const isOnward = (t) => {
    if (!t) return false;
    const s = String(t).toLowerCase();
    return s === "onword" || s === "onward" || s === "outbound";
  };
  const isReturn = (t) => {
    if (!t) return false;
    const s = String(t).toLowerCase();
    return s === "return" || s === "inbound";
  };

  const onwardSegs = segs.filter(s => isOnward(s.SegmentType));
  const returnSegs = segs.filter(s => isReturn(s.SegmentType));

  // Agar ye pure Return contract hai to returnSegs se data lo
  const primarySegs = onwardSegs.length > 0 ? onwardSegs : returnSegs;
  const isPureReturn = onwardSegs.length === 0 && returnSegs.length > 0;

  const connectionAirports = primarySegs.length > 1
    ? primarySegs.slice(0, -1).map(s => s.Destination).filter(Boolean)
    : [];
  const returnConnectionAirports = returnSegs.length > 1
    ? returnSegs.slice(0, -1).map(s => s.Destination).filter(Boolean)
    : [];

  const buildLayovers = (segs) => {
    if (segs.length <= 1) return [];
    const stops = [];
    for (let i = 0; i < segs.length - 1; i++) {
      const arrivingSeg = segs[i];
      const departingSeg = segs[i + 1];
      const mins = diffMinutes(arrivingSeg?.ArrivalDateTime, departingSeg?.DepartureDateTime);
      stops.push({
        airportCode: arrivingSeg?.Destination || departingSeg?.Origen || "",
        airportCity: arrivingSeg?.DestinationCity || departingSeg?.SourceCity || "",
        arriveTime: formatTime(arrivingSeg?.ArrivalDateTime),
        departTime: formatTime(departingSeg?.DepartureDateTime),
        durationMin: mins,
        durationLabel: formatLayover(mins),
      });
    }
    return stops;
  };

  const layovers = buildLayovers(onwardSegs.length ? onwardSegs : primarySegs);
  const retLayovers = buildLayovers(returnSegs);

  const seg     = primarySegs[0] || segs[0];
  const lastSeg = primarySegs[primarySegs.length - 1] || seg;

  const fare = resolveFare(flight);
  const returnSeg     = returnSegs[0];
  const lastReturnSeg = returnSegs[returnSegs.length - 1];

  const onwardStops = (onwardSegs.length || primarySegs.length) > 1
    ? (onwardSegs.length || primarySegs.length) - 1
    : 0;
  const returnStops = returnSegs.length > 1 ? returnSegs.length - 1 : 0;

  const onwardDur = flight?.ContractDuration?.find(d => isOnward(d.ContractType))?.TripDuration
    || seg?.Duration || "";
  const returnDur = flight?.ContractDuration?.find(d => isReturn(d.ContractType))?.TripDuration
    || returnSeg?.Duration || "";

  const firstFareGroup = (flight?.FareGroups?.FareGroup || [])[0];
  const grossPrice = firstPositive(
    fare?.GrossFare, fare?.TotalFare, fare?.TotalAmount, fare?.Total,
    flight?.GrossFare, flight?.TotalFare, flight?.TotalAmount, flight?.Price,
    toNum(fare?.BaseFare) + toNum(fare?.TaxFare ?? fare?.Tax ?? fare?.Taxes),
    firstFareGroup?.GrossFare,
    fare?.BaseFare
  );
  if (grossPrice === 0 && flight && !__priceWarned) {
    __priceWarned = true;
    // Debug: price 0 aaye to ye console me dikhega, is contract ka structure dekh kar sahi field map kar lena
    console.warn("[Index] Price 0 mila. Contract keys:", Object.keys(flight), "| fare object:", fare, "| contract:", flight);
  }
  const tds = fare?.TDS || fare?.TDSAmount || Math.round((grossPrice * TDS_RATE) / 100);

  return {
    airline:      seg?.AirlineName    || "Airline",
    airlineCode:  seg?.AirlineCode    || "",
    flightNo:     seg?.FlightNumber   || "",
    code:         `${seg?.AirlineCode || ""}-${seg?.FlightNumber || ""}`,
    logo:         (seg?.AirlineCode   || "?").substring(0, 2),
    dep:          formatTime(seg?.DepartureDateTime),
    arr:          formatTime(lastSeg?.ArrivalDateTime),
    depDate:      seg?.DepartureDateTime || "",
    durMin:       isPureReturn ? returnDur : onwardDur,
    totalDurMin:  durationToMinutes(onwardDur) + (returnSegs.length ? durationToMinutes(returnDur) : 0),
    stops:        isPureReturn ? returnStops : onwardStops,
    connectionAirports,
    retConnectionAirports: returnConnectionAirports,
    layovers,
    retLayovers,
    price:        grossPrice,
    baseFare:     fare?.BaseFare  || 0,
    tax:          fare?.TaxFare   || fare?.Tax || fare?.Taxes || 0,
    tdsAmount:    tds,
    netFare:      fare?.NetFare || (grossPrice - tds),
    baggage:      seg?.BaggageAllowed?.CheckInBaggage || "N/A",
    handBag:      seg?.BaggageAllowed?.HandBaggage    || "N/A",
    origin:       seg?.Origen         || "",
    originCity:   seg?.SourceCity     || "",
    dest:         lastSeg?.Destination     || "",
    destCity:     lastSeg?.DestinationCity || "",
    segType:      seg?.SegmentType    || "",
    isRoundTrip:  returnSegs.length > 0 && onwardSegs.length > 0, // only true for combined
    isPureReturn, // naya flag
    retDep:           formatTime(returnSeg?.DepartureDateTime),
    retArr:           formatTime(lastReturnSeg?.ArrivalDateTime),
    retDepDate:       returnSeg?.DepartureDateTime || "",
    retAirlineCode:   returnSeg?.AirlineCode || "",
    retFlightNo:      returnSeg?.FlightNumber || "",
    retCode:          `${returnSeg?.AirlineCode || ""}-${returnSeg?.FlightNumber || ""}`,
    retOrigin:        returnSeg?.Origen       || "",
    retOriginCity:    returnSeg?.SourceCity   || "",
    retDest:          lastReturnSeg?.Destination     || "",
    retDestCity:      lastReturnSeg?.DestinationCity || "",
    retDurMin:        returnDur,
    retStops:         returnStops,
    retBaggage:       returnSeg?.BaggageAllowed?.CheckInBaggage || "N/A",
    retHandBag:       returnSeg?.BaggageAllowed?.HandBaggage    || "N/A",
    fareGroups:       flight?.FareGroups?.FareGroup || [],
    refundable:       flight?.Refundable === true,
  };
};

const sortFlights = (list, tab) => {
  const items = list.map((f) => {
    const d = getDisplayData(f);
    return { f, price: d.price, dur: d.totalDurMin };
  });

  if (tab === "Cheapest") {
    items.sort((a, b) => cmpNum(a.price, b.price) || cmpNum(a.dur, b.dur));
  } else if (tab === "Fastest") {
    items.sort((a, b) => cmpNum(a.dur, b.dur) || cmpNum(a.price, b.price));
  } else if (items.length) {
    const prices = items.map((x) => x.price);
    const durs = items.map((x) => x.dur).filter(isFinite);
    const minP = Math.min(...prices), maxP = Math.max(...prices);
    const minD = durs.length ? Math.min(...durs) : 0;
    const maxD = durs.length ? Math.max(...durs) : 0;
    const norm = (v, min, max) => (max === min ? 0 : (v - min) / (max - min));
    const score = (x) =>
      norm(x.price, minP, maxP) * 0.5 +
      (isFinite(x.dur) ? norm(x.dur, minD, maxD) : 1) * 0.5; // weights yahan badal sakte hain
    items.sort((a, b) => score(a) - score(b));
  }
  return items.map((x) => x.f);
};

// ── Onward + Return pairing rules ──
// Rule 1: dono ki airline same honi chahiye
// Rule 2: return ki departure, onward ki arrival ke baad honi chahiye
const isOnwardT = (t) => ["onword", "onward", "outbound"].includes(String(t || "").toLowerCase());
const isReturnT = (t) => ["return", "inbound"].includes(String(t || "").toLowerCase());

// FIX: Response me alag-alag Onward aur Return contracts hain ya nahi (domestic round trip).
// International me combined fares aate hain (ek contract me onward + return segments),
// wahan ye false hoga.
const hasSplitContracts = (list) =>
  list.some((f) => isOnwardT(f.ContractType)) && list.some((f) => isReturnT(f.ContractType));

const getLegBounds = (flight, kind) => {
  const segs = (flight?.AirSegments || []).filter((s) =>
    kind === "onward" ? isOnwardT(s.SegmentType) : isReturnT(s.SegmentType)
  );
  const first = segs[0];
  const last = segs[segs.length - 1];
  return {
    airline: first?.AirlineCode || "",
    dep: first?.DepartureDateTime ? new Date(first.DepartureDateTime).getTime() : NaN,
    arr: last?.ArrivalDateTime ? new Date(last.ArrivalDateTime).getTime() : NaN,
  };
};

const isValidPair = (onwardFlight, returnFlight) => {
  const o = getLegBounds(onwardFlight, "onward");
  const r = getLegBounds(returnFlight, "return");
  if (!o.airline || o.airline !== r.airline) return false;
  if (!Number.isFinite(o.arr) || !Number.isFinite(r.dep)) return false;
  return r.dep > o.arr;
};
// Selected fare ko contract par apply karta hai, taaki FareDetails ko wahi fare mile jo click kiya
const applySelectedFare = (flight, fare, allContracts) => {
  if (!flight || !fare) return flight;

  // Agar us fare ka apna contract list me hai to wahi use karo
  const match = (allContracts || []).find(
    (c) => c.ContractId && c.ContractId === fare.ContractId
  );
  if (match) return match;

  // Warna same contract par fare ki values override karo
  const base = flight.AirlineFare || {};
  return {
    ...flight,
    SelectedFareId: fare.ContractId,
    Refundable: fare.IsRefundable ?? flight.Refundable,

    AirlineFare: {
      ...base,
      GrossFare: fare.GrossFare ?? base.GrossFare,
      BaseFare:  fare.BaseFare  ?? base.BaseFare,
      TaxFare:   fare.TaxFare   ?? base.TaxFare,
      NetFare:   fare.NetFare   ?? base.NetFare,
      TDS:       fare.TDS       ?? base.TDS,
    },
  };
};
const S = {
  page: { fontFamily: "'Manrope', 'Segoe UI', system-ui, sans-serif", background: "#F3F5F9", minHeight: "100vh", color: "#0F1B33" },
  topBar: { background: "#034494", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, maxWidth: 1600, margin: "0 auto", width: "100%", boxSizing: "border-box" },
  metaRow: { display: "flex", flex: 1, alignItems: "center", flexWrap: "wrap", gap: 32 },
  metaItem: { display: "flex", flexDirection: "column", gap: 3 },
  metaLabel: { fontSize: 11.5, color: "#9DB8E6", fontWeight: 600, letterSpacing: ".2px" },
  metaVal: { fontSize: 14, color: "#fff", fontWeight: 700 },
  topBtnGroup: { display: "flex", alignItems: "center", gap: 8 },
  modifyBtn: { background: "#E5246F", color: "#fff", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" },
  modPanel: { background: "#fff", borderBottom: "1px solid #E8EAF0", padding: "16px 20px" },
  mainArea: { display: "flex", gap: 20, padding: "20px 24px 32px", maxWidth: 1600, margin: "0 auto", width: "100%", boxSizing: "border-box" },
  filterCard: { width: 250, flexShrink: 0, background: "#fff", border: "1px solid #E8EAF0", borderRadius: 12, overflow: "hidden", alignSelf: "start", boxShadow: "0 1px 3px rgba(15,27,51,.05)" },
  filterHead: { padding: "14px 18px", background: "#0B2A5E", display: "flex", justifyContent: "space-between", alignItems: "center" },
  filterHeadTitle: { fontSize: 14.5, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 8 },
  resetLink: { fontSize: 12, color: "#fff", cursor: "pointer", background: "rgba(255,255,255,.14)", border: "none", fontWeight: 600, padding: "5px 12px", borderRadius: 6 },
  filterSec: { padding: "16px 18px", borderBottom: "1px solid #EEF1F6" },
  filterSecHead: { display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", marginBottom: 12 },
  filterTitle: { fontSize: 13.5, fontWeight: 700, color: "#0F1B33", display: "flex", alignItems: "center", gap: 8 },
  chevron: { fontSize: 9, color: "#6B7385", transition: "transform .2s ease" },
  stopBtns: { display: "flex", gap: 8 },
  resultsArea: { flex: 1, minWidth: 0, maxWidth: 1360 },
  sortTabs: { display: "flex", background: "#fff", border: "1px solid #E8EAF0", borderRadius: 12, overflow: "hidden", marginBottom: 12, boxShadow: "0 1px 3px rgba(15,27,51,.05)" },
  sortTab: { flex: 1, padding: "12px 8px", textAlign: "center", cursor: "pointer", fontSize: 12 },
  navBtns: { display: "flex", gap: 10, marginBottom: 12, alignItems: "center", flexWrap: "wrap" },
  navBtn: { padding: "7px 16px", border: "1px solid #D5DBE8", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#3B4663" },
  // View toggle (Split / Combined) aur TDS button
  toggleBtn: { padding: "7px 16px", border: "1.5px solid #1a73e8", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", background: "#fff", color: "#1a73e8" },
  toggleBtnOn: { padding: "7px 16px", border: "1.5px solid #1a73e8", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", background: "#1a73e8", color: "#fff" },
  colHead: { display: "grid", gridTemplateColumns: "2fr 1.1fr 1.2fr 1.1fr 1.2fr", gap: 8, padding: "4px 20px", fontSize: 12, color: "#6B7385", fontWeight: 600, marginBottom: 6 },
  flightCard: { background: "#fff", border: "1px solid #E8EAF0", borderRadius: 12, marginBottom: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(15,27,51,.05)" },
  flightRow: { display: "grid", gridTemplateColumns: "2fr 1.1fr 1.2fr 1.1fr 1.2fr", gap: 8, padding: "18px 20px", alignItems: "center" },
  returnRow: { display: "grid", gridTemplateColumns: "2fr 1.1fr 1.2fr 1.1fr 1.2fr", gap: 8, padding: "14px 20px", alignItems: "center", background: "#F7F9FD", borderTop: "1px solid #EAEEF6" },
  detailBar: { borderTop: "1px solid #EEF1F6", padding: "10px 20px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", background: "#FBFCFE" },
  timeBig: { fontSize: 21, fontWeight: 800, color: "#0F1B33", letterSpacing: "-.3px" },
  timeSub: { fontSize: 12, fontWeight: 700, color: "#3B4663" },
  priceAmt: { fontSize: 24, fontWeight: 800, color: "#0F1B33", letterSpacing: "-.4px" },
  priceSub: { fontSize: 11.5, color: "#6B7385", marginBottom: 4 },
  // Amount aur TDS badge ek hi line me (side by side)
  priceRow: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" },
  tdsBadge: {
    display: "inline-flex", flexDirection: "row", alignItems: "center", gap: 6,
    border: "1px solid #BFD3F7", borderRadius: 6, padding: "2px 8px", background: "#F0F6FF",
  },
  tdsPill: {
    fontSize: 10, fontWeight: 800, color: "#fff", background: "#1a73e8",
    padding: "1px 6px", borderRadius: 4, letterSpacing: ".3px",
  },
  tdsAmt: { fontSize: 13, fontWeight: 700, color: "#1a73e8" },
  tdsAmtLight: { fontSize: 13, fontWeight: 700, color: "#BFE0FF" },
  bookBtn: {
    background: "#1a73e8",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 13.5,
    fontWeight: 700,
    cursor: "pointer",
    width: "50%",
    transition: "background .15s ease",
  },
  lockPriceBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    padding: "6px 12px",
    fontSize: 11.5,
    fontWeight: 700,
    color: "#B45309",
    background: "#FFFBF2",
    border: "1px solid #F5D49A",
    borderRadius: 8,
    cursor: "pointer",
    width: 118,
    whiteSpace: "nowrap",
    transition: "background .15s ease",
  },
  badgeGreen: { background: "#E6F5EC", color: "#0B6B3A", padding: "3px 9px", borderRadius: 6, fontSize: 11.5, fontWeight: 600 },
  badgeGray: { background: "#EEF1F6", color: "#3B4663", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500 },
  badgeAmber: { background: "#FFF3DC", color: "#8A4B00", padding: "3px 9px", borderRadius: 6, fontSize: 11.5, fontWeight: 600 },
  tripLabel: { fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, marginBottom: 4, display: "inline-block" },
  logoBox: { width: 40, height: 40, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 },
  logoImg: { width: "100%", height: "100%", objectFit: "contain" },
  loadingMore: { textAlign: "center", padding: "16px 0", color: "#6B7385", fontSize: 12.5 },
  // Split view
  splitContainer: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" },
  splitColumn: {
    display: "flex", flexDirection: "column", minWidth: 0, background: "#fff",
    border: "1px solid #E8EAF0", borderRadius: 12, maxHeight: 680, overflowY: "auto", overflowX: "hidden",
    boxShadow: "0 1px 3px rgba(15,27,51,.05)",
  },
  splitColumnHeader: {
    display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 800, color: "#0F1B33",
    background: "#fff", borderBottom: "1px solid #E8EAF0", padding: "14px 16px",
    position: "sticky", top: 0, zIndex: 2,
  },
  splitColumnBody: { padding: "12px" },
  legCard: { background: "#fff", border: "1.5px solid #E8EAF0", borderRadius: 12, marginBottom: 10, overflow: "hidden", cursor: "pointer", transition: "border-color .15s ease, box-shadow .15s ease" },
  legCardSelected: { background: "#F8FBFF", border: "1.5px solid #1a73e8", borderRadius: 12, marginBottom: 10, overflow: "hidden", cursor: "pointer", boxShadow: "0 0 0 3px rgba(26,115,232,.12)" },
  legRow: { display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 8, padding: "16px", alignItems: "center" },
  legBookRow: { borderTop: "1px solid #EEF1F6", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 },
  selectBtn: { background: "#fff", color: "#1a73e8", border: "1.5px solid #1a73e8", borderRadius: 8, padding: "7px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  selectBtnActive: { background: "#1a73e8", color: "#fff", border: "1.5px solid #1a73e8", borderRadius: 8, padding: "7px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  // Fixed bottom booking bar
  bookingBarWrap: {
    position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 500,
    background: "#0B2150",
    borderTop: "1px solid rgba(255,255,255,.12)",
    boxShadow: "0 -8px 28px rgba(8,18,45,.3)",
  },
  bookingBarInner: {
    maxWidth: 1600, margin: "0 auto", padding: "12px 24px",
    display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
  },
  bookingLegChip: { display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 240 },
  bookingLegTag: { fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 4, color: "#fff", flexShrink: 0 },
  bookingLegRouteName: { fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 2 },
  bookingLegMeta: { fontSize: 11.5, color: "#B5C4E4", marginBottom: 3 },
  bookingLegRoute: { display: "flex", alignItems: "center", gap: 8, color: "#fff", fontWeight: 700, fontSize: 14.5 },
  bookingLegPrice: { color: "#fff", fontWeight: 800, fontSize: 15, marginLeft: "auto", whiteSpace: "nowrap" },
  bookingDivider: { width: 1, alignSelf: "stretch", background: "rgba(255,255,255,.16)" },
  bookingTotalBlock: { textAlign: "right", color: "#fff", flexShrink: 0 },
  bookingTotalLabel: { fontSize: 11.5, color: "#B5C4E4" },
  bookingTotalAmt: { fontSize: 22, fontWeight: 800, letterSpacing: "-.3px" },
  bookNowBtn: { background: "#2E7BF6", color: "#fff", border: "none", borderRadius: 8, padding: "13px 28px", fontSize: 14, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 },
  bookingClearBtn: { background: "transparent", color: "#D3DDF2", border: "1px solid rgba(255,255,255,.28)", borderRadius: 8, padding: "11px 16px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", flexShrink: 0 },
};

const LOGO_COLORS = ["#D0021B","#1a73e8","#FF6600","#6B21A8","#059669","#e91e63","#0097A7","#F57C00"];

const IconPlane = ({ style }) => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" style={style} aria-hidden="true">
    <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2.5 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
  </svg>
);
const IconCalendar = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IconUsers = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconSwap = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 16V4M7 4L3 8M7 4l4 4" /><path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
  </svg>
);
const IconSearch = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.3-4.3" />
  </svg>
);
const IconFilter = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);
const IconRupee = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12M6 8h12M6 13l8.5 8M6 13h3c3 0 5-1.5 5-5" />
  </svg>
);
const IconStops = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
  </svg>
);
const IconClock = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconAirlineTag = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.5 3.5L18 11l3 3v3l-6-2-2 5-1.5-1.5L13 15l-8-2.5V10l4 1L11.5 3l1.5 1.5L11 8l3 1z" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

async function loadAirportsShared(setAirports) {
  const stored = localStorage.getItem("airports");
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        setAirports(parsed);
        return;
      }
    } catch (e) {
      localStorage.removeItem("airports");
    }
  }

  try {
    const AIRPORTS_ENDPOINT = process.env.NODE_ENV === "development"
      ? "/api/Air/Airports"
      : `${process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_FLIGHTS_API_URL}/Air/Airports`;

    const res = await fetch(AIRPORTS_ENDPOINT, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (res.ok) {
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
      if (arr.length > 0) {
        setAirports(arr);
        localStorage.setItem("airports", JSON.stringify(arr));
        return;
      }
    }
  } catch (err) {
    console.warn("Airport GET fetch failed, trying POST fallback:", err.message);
  }

  try {
    // FIX: sahi URL (space hata diya aur endpoint Air/Airports kiya)
    const res = await fetch("https://promo.namantechnolab.com/api/Air/Airports", {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (res.ok) {
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
      if (arr.length > 0) {
        setAirports(arr);
        localStorage.setItem("airports", JSON.stringify(arr));
        return;
      }
    }
  } catch (err) {
    console.error("Airport POST fallback also failed:", err.message);
  }
}

function RouteLine() {
  return (
    <div style={{ display: "flex", alignItems: "center", margin: "8px 6px", height: 14 }} aria-hidden="true">
      <span style={{ width: 7, height: 7, borderRadius: "50%", border: "1.5px solid #8B97B2", background: "#fff", flexShrink: 0 }} />
      <span style={{ flex: 1, height: 1.5, background: "#C9D1E3" }} />
      <span style={{ color: "#1a73e8", display: "flex", margin: "0 4px" }}><IconPlane style={{ transform: "rotate(90deg)" }} /></span>
      <span style={{ flex: 1, height: 1.5, background: "#C9D1E3" }} />
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#8B97B2", flexShrink: 0 }} />
    </div>
  );
}

function TdsBadge({ amount, light }) {
  return (
    <div style={S.tdsBadge}>
      <span style={S.tdsPill}>TDS</span>
      <span style={light ? S.tdsAmtLight : S.tdsAmt}>₹{Math.round(amount).toLocaleString("en-IN")}</span>
    </div>
  );
}

function AirlineLogo({ airlineCode, bgColor, textColor, initials }) {
  const [imgError, setImgError] = useState(false);
  const fileName = getLogoFileName(airlineCode);

  if (imgError || !airlineCode) {
    return (
      <div style={{ ...S.logoBox, background: bgColor + "22", color: textColor, fontSize: 10, fontWeight: 700 }}>
        {initials}
      </div>
    );
  }

  return (
    <div style={{ ...S.logoBox, background: "#fff", border: "1px solid #F0F1F5" }}>
      <img
        src={`/Flight_Images/${fileName}.gif`}
        alt={airlineCode}
        style={S.logoImg}
        onError={() => setImgError(true)}
      />
    </div>
  );
}

function ModifyPanel({ tripType, setTripType, navigate, searchData }) {
  const firstRoute = searchData?.Routs?.[0];
  const secondRoute = searchData?.Routs?.[1];
  const CLASS_NAMES_REVERSE = {
    0: "Economy",
    1: "Premium Economy",
    2: "Business",
    3: "First Class",
  };
  const initialFrom = {
    city: firstRoute?.InputSource?.split(" - ")[1] || firstRoute?.Source || "",
    code: firstRoute?.Source || "",
    name: firstRoute?.InputSource?.split(" - ")[1] || "",
  };
  const initialTo = {
    city: firstRoute?.OutputSource?.split(" - ")[1] || firstRoute?.Destination || "",
    code: firstRoute?.Destination || "",
    name: firstRoute?.OutputSource?.split(" - ")[1] || "",
  };

  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [fromQuery, setFromQuery] = useState(initialFrom.city);
  const [toQuery, setToQuery] = useState(initialTo.city);
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [airports, setAirports] = useState([]);
  const [departDate, setDepartDate] = useState(firstRoute?.TravelDate || "");
  const [returnDate, setReturnDate] = useState(secondRoute?.TravelDate || "");
  const [adults, setAdults] = useState(searchData?.AdultCount || 1);
  const [children, setChildren] = useState(searchData?.ChildCount || 0);
  const [infants, setInfants] = useState(searchData?.InfantCount || 0);
  const [travelClass, setTravelClass] = useState(
    CLASS_NAMES_REVERSE[searchData?.TypeOfClass] || "Economy"
  );
  const [showTravelerDropdown, setShowTravelerDropdown] = useState(false);

  const fromRef = useRef(null);
  const toRef = useRef(null);
  const travelerRef = useRef(null);
  const departInputRef = useRef(null);
  const returnInputRef = useRef(null);

  useEffect(() => {
    setFrom(initialFrom);
    setTo(initialTo);
    setFromQuery(initialFrom.city);
    setToQuery(initialTo.city);
    setDepartDate(firstRoute?.TravelDate || "");
    setReturnDate(secondRoute?.TravelDate || "");
    setAdults(searchData?.AdultCount || 1);
    setChildren(searchData?.ChildCount || 0);
    setInfants(searchData?.InfantCount || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  useEffect(() => {
    loadAirportsShared(setAirports);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fromRef.current && !fromRef.current.contains(e.target)) setFromSuggestions([]);
      if (toRef.current && !toRef.current.contains(e.target)) setToSuggestions([]);
      if (travelerRef.current && !travelerRef.current.contains(e.target)) setShowTravelerDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchData?.TripType === 1 || (searchData?.Routs?.length || 0) > 1) {
      setTripType("Round trip");
    } else {
      setTripType("One way");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  const getFilteredAirports = (query) => {
    if (!Array.isArray(airports)) return [];
    const q = (query || "").trim().toLowerCase();
    return airports.filter(a =>
      (a.CityName && a.CityName.toLowerCase().includes(q)) ||
      (a.AirportCode && a.AirportCode.toLowerCase().includes(q)) ||
      (a.AirportName && a.AirportName.toLowerCase().includes(q))
    );
  };

  const handleFromChange = (e) => {
    const value = e.target.value;
    setFromQuery(value);
    setFromSuggestions(value.trim().length >= 3 ? getFilteredAirports(value).slice(0, 10) : []);
  };

  const handleToChange = (e) => {
    const value = e.target.value;
    setToQuery(value);
    setToSuggestions(value.trim().length >= 3 ? getFilteredAirports(value).slice(0, 10) : []);
  };

  const handleFromFocus = () => {
    if (fromQuery.trim().length >= 3) setFromSuggestions(getFilteredAirports(fromQuery).slice(0, 10));
  };
  const handleToFocus = () => {
    if (toQuery.trim().length >= 3) setToSuggestions(getFilteredAirports(toQuery).slice(0, 10));
  };

  const selectFromAirport = (a) => {
    setFrom({ city: a.CityName, code: a.AirportCode, name: a.AirportName });
    setFromQuery(a.CityName);
    setFromSuggestions([]);
  };

  const selectToAirport = (a) => {
    setTo({ city: a.CityName, code: a.AirportCode, name: a.AirportName });
    setToQuery(a.CityName);
    setToSuggestions([]);
  };

  const handleSwap = () => {
    const tf = from, tt = to;
    setFrom(tt); setTo(tf);
    setFromQuery(tt.city); setToQuery(tf.city);
  };

  const fmtDateDisplay = (dateStr) => {
    if (!dateStr) return null;
    const dt = new Date(dateStr);
    if (isNaN(dt.getTime())) return null;
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    return { day: dt.getDate(), month: months[dt.getMonth()], year: dt.getFullYear(), dayName: days[dt.getDay()] };
  };
  const departDisp = fmtDateDisplay(departDate);
  const returnDisp = fmtDateDisplay(returnDate);
  const totalTravelers = adults + children + infants;

  const CLASS_MAP = { "Economy": 0, "Premium Economy": 1, "Business": 2, "First Class": 3 };

  // FIX: IsDomestic pehle hamesha false jata tha. Ab airports ke country se detect hota hai,
  // country field na mile to original search ki value use hoti hai.
  // NOTE: airports API me country ka field naam alag ho to yahan badal lena.
  const isIndianAirport = (code) => isIndianAirportCode(code, airports);

  const detectDomestic = () => {
    const f = isIndianAirport(from.code);
    const t = isIndianAirport(to.code);
    if (f !== null && t !== null) return f && t;
    return searchData?.IsDomestic ?? true;
  };

  const handleSearch = () => {
    const routs = [{
      Source: from.code,
      InputSource: `${from.code} - ${from.name}`,
      Destination: to.code,
      OutputSource: `${to.code} - ${to.name}`,
      TravelDate: departDate,
    }];
    if (tripType === "Round trip") {
      routs.push({
        Source: to.code,
        InputSource: `${to.code} - ${to.name}`,
        Destination: from.code,
        OutputSource: `${from.code} - ${from.name}`,
        TravelDate: returnDate,
      });
    }
    const payload = {
      APIToken: null,
      AgencyKey: "02AD68F3-9AE6-4148-85B1-E61D1F03991F",
      AdultCount: Number(adults) || 1,
      ChildCount: Number(children) || 0,
      InfantCount: Number(infants) || 0,
      PreferredAirlines: null,
      Routs: routs,
      TripType: tripType === "Round trip" ? 1 : 0,
      TypeOfClass: CLASS_MAP[travelClass] ?? 0,
      IsDomestic: detectDomestic(),
      SetAirport: null,
      IsB2B: false,
      IsSpecailFlight: false,
      FromCity: null,
      Tocity: null,
      TransactionId: 0,
      EngineType: 0,
      // Original search ka AccountNo reuse karo (home page se 11 aa raha hai)
      AccountNo: searchData?.AccountNo ?? 17,
    };
    navigate("/AirResponse/Index", { state: { searchData: payload } });
  };

  return (
    <div className="mp-panel">
      <div className="mp-triptype" role="tablist" aria-label="Trip type">
        {["One way", "Round trip"].map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tripType === t}
            className={`mp-trip-btn${tripType === t ? " active" : ""}`}
            onClick={() => setTripType(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mp-fields">
        <div className="mp-field mp-field-route" ref={fromRef}>
          <label className="mp-label"><IconPlane style={{ transform: "rotate(45deg)" }} /> From</label>
          <input
            className="mp-input"
            value={fromQuery}
            onChange={handleFromChange}
            onFocus={handleFromFocus}
            autoComplete="off"
            placeholder="City or airport"
          />
          <span className="mp-sub">{from.code} · {from.name}</span>

          {fromSuggestions.length > 0 && (
            <ul className="mp-suggestions">
              {fromSuggestions.map((a, i) => (
                <li key={a.ID || i} onClick={() => selectFromAirport(a)}>
                  <span className="mp-sugg-main">{a.CityName} <span className="mp-sugg-code">{a.AirportCode}</span></span>
                  <span className="mp-sugg-sub">{a.AirportName}</span>
                </li>
              ))}
            </ul>
          )}

          <button type="button" className="mp-swap" onClick={handleSwap} aria-label="Swap origin and destination">
            <IconSwap />
          </button>
        </div>

        <div className="mp-field mp-field-route" ref={toRef}>
          <label className="mp-label"><IconPlane style={{ transform: "rotate(135deg)" }} /> To</label>
          <input
            className="mp-input"
            value={toQuery}
            onChange={handleToChange}
            onFocus={handleToFocus}
            autoComplete="off"
            placeholder="City or airport"
          />
          <span className="mp-sub">{to.code} · {to.name}</span>

          {toSuggestions.length > 0 && (
            <ul className="mp-suggestions">
              {toSuggestions.map((a, i) => (
                <li key={a.ID || i} onClick={() => selectToAirport(a)}>
                  <span className="mp-sugg-main">{a.CityName} <span className="mp-sugg-code">{a.AirportCode}</span></span>
                  <span className="mp-sugg-sub">{a.AirportName}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mp-field mp-field-date" onClick={() => departInputRef.current?.showPicker?.()}>
          <label className="mp-label"><IconCalendar /> Departure</label>
          <input
            ref={departInputRef}
            type="date"
            className="mp-date-input"
            value={departDate}
            onChange={(e) => setDepartDate(e.target.value)}
          />
          {departDisp ? (
            <div className="mp-date-display">
              <span className="mp-date-day">{departDisp.day}</span>
              <span className="mp-date-month">{departDisp.month} {departDisp.year}</span>
            </div>
          ) : (
            <div className="mp-date-placeholder">Select date</div>
          )}
          <span className="mp-sub">{departDisp?.dayName || "\u00A0"}</span>
        </div>

        <div
          className={`mp-field mp-field-date${tripType !== "Round trip" ? " mp-disabled" : ""}`}
          onClick={() => tripType === "Round trip" && returnInputRef.current?.showPicker?.()}
        >
          <label className="mp-label"><IconCalendar /> Return</label>
          <input
            ref={returnInputRef}
            type="date"
            className="mp-date-input"
            value={returnDate}
            disabled={tripType !== "Round trip"}
            min={departDate}
            onChange={(e) => setReturnDate(e.target.value)}
          />
          {tripType === "Round trip" && returnDisp ? (
            <div className="mp-date-display">
              <span className="mp-date-day">{returnDisp.day}</span>
              <span className="mp-date-month">{returnDisp.month} {returnDisp.year}</span>
            </div>
          ) : (
            <div className="mp-date-placeholder">{tripType === "Round trip" ? "Select date" : "One way trip"}</div>
          )}
          <span className="mp-sub">{(tripType === "Round trip" && returnDisp?.dayName) || "\u00A0"}</span>
        </div>

        <div className="mp-field mp-field-travelers" ref={travelerRef} onClick={() => setShowTravelerDropdown(v => !v)}>
          <label className="mp-label"><IconUsers /> Travellers &amp; class</label>
          <div className="mp-date-display">
            <span className="mp-date-day">{totalTravelers}</span>
            <span className="mp-date-month">Traveller{totalTravelers > 1 ? "s" : ""}</span>
          </div>
          <span className="mp-sub">{travelClass}</span>

          {showTravelerDropdown && (
            <div className="mp-traveler-dropdown" onClick={(e) => e.stopPropagation()}>
              {[
                { label: "Adults", sub: "12+ yrs", val: adults, set: setAdults },
                { label: "Children", sub: "2–12 yrs", val: children, set: setChildren },
                { label: "Infants", sub: "Under 2 yrs", val: infants, set: setInfants },
              ].map((item, idx) => {
                const isAdultOrChild = item.label === "Adults" || item.label === "Children";
                const canIncrease = isAdultOrChild ? (adults + children < 9) : (infants < 9);
                const canDecrease = item.label === "Adults" ? item.val > 1 : item.val > 0;

                return (
                  <div key={idx} className="mp-traveler-row">
                    <div>
                      <div className="mp-traveler-label">{item.label}</div>
                      <div className="mp-traveler-sub">{item.sub}</div>
                    </div>
                    <div className="mp-stepper">
                      <button type="button" disabled={!canDecrease} onClick={() => item.set(item.val - 1)}>&minus;</button>
                      <span>{item.val}</span>
                      <button type="button" disabled={!canIncrease} onClick={() => item.set(item.val + 1)}>+</button>
                    </div>
                  </div>
                );
              })}

              <div className="mp-class-section">
                <div className="mp-class-title">Travel class</div>
                <div className="mp-class-grid">
                  {["Economy", "Premium Economy", "Business", "First Class"].map((cls) => (
                    <button
                      key={cls}
                      type="button"
                      className={`mp-class-btn${travelClass === cls ? " active" : ""}`}
                      onClick={() => setTravelClass(cls)}
                    >
                      {cls}
                    </button>
                  ))}
                </div>
              </div>

              <button type="button" className="mp-done-btn" onClick={() => setShowTravelerDropdown(false)}>
                Done
              </button>
            </div>
          )}
        </div>

        <div className="mp-field mp-field-submit">
          <button type="button" className="mp-search-btn" onClick={handleSearch}>
            <IconSearch /> Search
          </button>
        </div>
      </div>

      <style>{`
  .mp-panel {
    --mp-navy: #0B1D45;
    --mp-navy-deep: #071530;
    --mp-sky: #1a73e8;
    --mp-accent: #e91e63;
    --mp-accent-soft: #fff0f5;
    --mp-ink: #14213D;
    --mp-muted: #6B7280;
    --mp-border: #E5E7EB;
    --mp-bg: #F8FAFC;
    --mp-white: #ffffff;
    --mp-shadow: 0 10px 40px rgba(11, 29, 69, 0.12);

    background: var(--mp-white);
    border-bottom: 1px solid var(--mp-border);
    padding: 20px 24px 24px;
    max-width: 1600px;
    margin: 0 auto;
    box-shadow: var(--mp-shadow);
  }
  .mp-triptype {
    display: inline-flex; background: var(--mp-bg); border-radius: 12px; padding: 4px;
    margin-bottom: 18px; gap: 4px; border: 1px solid var(--mp-border);
  }
  .mp-trip-btn {
    border: none; background: transparent; padding: 9px 22px; border-radius: 9px;
    font-size: 13.5px; font-weight: 600; color: var(--mp-muted); cursor: pointer;
    transition: all 0.2s ease; font-family: inherit;
  }
  .mp-trip-btn.active { background: var(--mp-navy); color: #fff; box-shadow: 0 4px 14px rgba(11, 29, 69, 0.3); }
  .mp-trip-btn:hover:not(.active) { color: var(--mp-ink); background: rgba(11, 29, 69, 0.05); }
  .mp-fields {
    display: grid; grid-template-columns: 1.35fr 1.35fr 1fr 1fr 1.2fr auto;
    border: 1px solid var(--mp-border); border-radius: 16px; overflow: visible;
    background: var(--mp-white); box-shadow: 0 2px 12px rgba(11, 29, 69, 0.04);
  }
  .mp-field { position: relative; padding: 14px 18px; border-right: 1px solid var(--mp-border); transition: background 0.15s ease; }
  .mp-field:last-child { border-right: none; }
  .mp-field:not(.mp-field-submit):hover { background: #FAFBFF; }
  .mp-field.mp-disabled { opacity: 0.5; pointer-events: none; }
  .mp-label {
    display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em; color: var(--mp-muted); margin-bottom: 6px;
  }
  .mp-label svg { flex-shrink: 0; opacity: 0.7; }
  .mp-input {
    border: none; outline: none; width: 100%; font-size: 17px; font-weight: 700;
    color: var(--mp-ink); background: transparent; padding: 0; font-family: inherit;
  }
  .mp-input::placeholder { color: #B0B7C3; font-weight: 500; }
  .mp-sub {
    display: block; font-size: 11.5px; color: var(--mp-muted); margin-top: 3px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .mp-swap {
    position: absolute; right: -15px; top: 42px; width: 30px; height: 30px; border-radius: 50%;
    background: var(--mp-navy); color: #fff; border: 3px solid #fff; display: flex;
    align-items: center; justify-content: center; cursor: pointer; z-index: 5;
    box-shadow: 0 4px 12px rgba(11, 29, 69, 0.35);
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .mp-swap:hover { transform: rotate(180deg); }
  .mp-suggestions {
    position: absolute; top: calc(100% + 8px); left: 8px; right: 8px; z-index: 60;
    background: #fff; border: 1px solid var(--mp-border); border-radius: 14px; list-style: none;
    margin: 0; padding: 8px; max-height: 280px; overflow-y: auto;
    box-shadow: 0 16px 40px rgba(11, 29, 69, 0.18);
  }
  .mp-suggestions li {
    cursor: pointer; padding: 10px 12px; border-radius: 10px; display: flex;
    flex-direction: column; gap: 2px; transition: background 0.15s ease;
  }
  .mp-suggestions li:hover { background: var(--mp-bg); }
  .mp-sugg-main { font-size: 14px; font-weight: 600; color: var(--mp-ink); }
  .mp-sugg-code { color: var(--mp-sky); font-weight: 700; }
  .mp-sugg-sub { font-size: 12px; color: var(--mp-muted); }
  .mp-field-date { cursor: pointer; }
  .mp-date-input { position: absolute; inset: 0; opacity: 0; width: 100%; height: 100%; cursor: pointer; }
  .mp-date-display { display: flex; align-items: baseline; gap: 6px; }
  .mp-date-day { font-size: 22px; font-weight: 700; color: var(--mp-ink); }
  .mp-date-month { font-size: 13px; font-weight: 600; color: var(--mp-muted); }
  .mp-date-placeholder { font-size: 15px; font-weight: 600; color: #B0B7C3; padding-top: 2px; }
  .mp-field-travelers { cursor: pointer; }
  .mp-traveler-dropdown {
    position: absolute; top: calc(100% + 10px); right: 0; z-index: 70; background: #fff;
    border: 1px solid var(--mp-border); border-radius: 16px;
    box-shadow: 0 20px 50px rgba(11, 29, 69, 0.2); width: 320px; padding: 20px; cursor: default;
  }
  .mp-traveler-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; }
  .mp-traveler-label { font-size: 14.5px; font-weight: 600; color: var(--mp-ink); }
  .mp-traveler-sub { font-size: 12.5px; color: var(--mp-muted); }
  .mp-stepper { display: flex; align-items: center; gap: 14px; }
  .mp-stepper button {
    width: 30px; height: 30px; border-radius: 50%; border: 1.5px solid var(--mp-border);
    background: #fff; color: var(--mp-navy); font-size: 16px; line-height: 1; cursor: pointer;
    display: flex; align-items: center; justify-content: center; transition: all 0.15s ease;
  }
  .mp-stepper button:hover:not(:disabled) { background: var(--mp-navy); color: #fff; border-color: var(--mp-navy); }
  .mp-stepper button:disabled { opacity: 0.35; cursor: not-allowed; }
  .mp-stepper span { min-width: 20px; text-align: center; font-weight: 700; font-size: 15px; color: var(--mp-ink); }
  .mp-class-section { margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--mp-border); }
  .mp-class-title { font-size: 13.5px; font-weight: 700; color: var(--mp-ink); margin-bottom: 12px; }
  .mp-class-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .mp-class-btn {
    padding: 10px; border-radius: 10px; border: 1.5px solid var(--mp-border); background: #fff;
    font-size: 13px; font-weight: 600; color: var(--mp-ink); cursor: pointer;
    transition: all 0.15s ease; font-family: inherit;
  }
  .mp-class-btn.active { background: var(--mp-navy); border-color: var(--mp-navy); color: #fff; box-shadow: 0 4px 12px rgba(11, 29, 69, 0.25); }
  .mp-class-btn:hover:not(.active) { border-color: var(--mp-navy); background: #F0F4FF; }
  .mp-done-btn {
    margin-top: 18px; width: 100%; padding: 12px; border: none; border-radius: 10px;
    background: var(--mp-navy); color: #fff; font-size: 14.5px; font-weight: 700; cursor: pointer;
    font-family: inherit; transition: background 0.15s ease;
  }
  .mp-done-btn:hover { background: var(--mp-navy-deep); }
  .mp-field-submit { padding: 12px; display: flex; align-items: center; }
  .mp-search-btn {
    display: flex; align-items: center; justify-content: center; gap: 8px; height: 54px;
    padding: 0 28px; border: none; border-radius: 12px;
    background: linear-gradient(135deg, #e91e63, #c2185b); color: #fff; font-size: 15px;
    font-weight: 700; cursor: pointer; white-space: nowrap;
    box-shadow: 0 8px 24px rgba(233, 30, 99, 0.35); transition: all 0.2s ease; font-family: inherit;
  }
  .mp-search-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(233, 30, 99, 0.45); }
  .mp-field-route:focus-within, .mp-field-date:focus-within { box-shadow: inset 0 0 0 2px rgba(26, 115, 232, 0.18); }
  @media (max-width: 1024px) {
    .mp-fields { grid-template-columns: 1fr 1fr; }
    .mp-field { border-right: none; border-bottom: 1px solid var(--mp-border); }
    .mp-field:last-child { border-bottom: none; }
    .mp-field-submit { grid-column: 1 / -1; }
    .mp-search-btn { width: 100%; }
    .mp-swap { right: 12px; top: -14px; }
  }
  @media (max-width: 560px) {
    .mp-fields { grid-template-columns: 1fr; }
    .mp-traveler-dropdown { right: auto; left: 0; width: 100%; }
    .mp-panel { padding: 16px; }
  }
`}</style>
    </div>
  );
}

function FilterPanel({ filters, setFilters, availableAirlines, priceRange }) {
  const [openSecs, setOpenSecs] = useState({ price: true, stops: true, time: true, airlines: true, refundable: true });

  const toggle = (key, val) =>
    setFilters((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const resetAll = () => setFilters({ maxPrice: priceRange.max, stops: [], depSlots: [], airlines: [], refundable: [] });
  const toggleSec = (sec) => setOpenSecs((s) => ({ ...s, [sec]: !s[sec] }));

  const activeCount = filters.stops.length + filters.depSlots.length + filters.airlines.length + filters.refundable.length;

  const currencySymbol = "\u20b9";
  const fmt = (val) => Math.round(val).toLocaleString("en-IN");

  return (
    <div style={S.filterCard} className="td-filter">
      <div style={S.filterHead}>
        <span style={S.filterHeadTitle}><IconFilter /> Filters{activeCount > 0 && (
          <span style={{ background: "#e91e63", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "1px 7px" }}>
            {activeCount}
          </span>
        )}</span>
        <button style={S.resetLink} onClick={resetAll}>↺ Reset</button>
      </div>

      <div style={S.filterSec}>
        <div style={S.filterSecHead} onClick={() => toggleSec("price")}>
          <span style={S.filterTitle}><IconRupee /> Price</span>
          <span style={{ ...S.chevron, transform: openSecs.price ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
        </div>
        {openSecs.price && (
          <>
            <input
              type="range"
              min={priceRange.min}
              max={priceRange.max}
              step={priceRange.max > priceRange.min ? (priceRange.max - priceRange.min) / 100 : 1}
              value={filters.maxPrice}
              onChange={(e) => setFilters((f) => ({ ...f, maxPrice: +e.target.value }))}
              style={{ width: "100%", accentColor: "#1a73e8" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 10.5, color: "#6B7385", fontWeight: 500 }}>
                {currencySymbol}{fmt(priceRange.min)}
              </span>
              <span style={{
                fontSize: 12, color: "#1a73e8", fontWeight: 700, background: "#E8F0FE",
                padding: "3px 10px", borderRadius: 20,
              }}>
                {currencySymbol}{fmt(filters.maxPrice)}
              </span>
            </div>
          </>
        )}
      </div>

      <div style={S.filterSec}>
        <div style={S.filterSecHead} onClick={() => toggleSec("stops")}>
          <span style={S.filterTitle}><IconStops /> Stops</span>
          <span style={{ ...S.chevron, transform: openSecs.stops ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
        </div>
        {openSecs.stops && (
          <div style={S.stopBtns}>
            {[0, 1, 2].map((s) => {
              const active = filters.stops.includes(s);
              return (
                <div
                  key={s}
                  onClick={() => toggle("stops", s)}
                  style={{
                    flex: 1,
                    padding: "9px 4px",
                    border: `1.5px solid ${active ? "#1a73e8" : "#E8EAF0"}`,
                    borderRadius: 10,
                    fontSize: 12,
                    textAlign: "center",
                    cursor: "pointer",
                    background: active ? "linear-gradient(135deg, #1a73e8, #4a90f0)" : "#fff",
                    color: active ? "#fff" : "#4A5670",
                    fontWeight: active ? 700 : 500,
                    transition: "all .18s ease",
                    boxShadow: active ? "0 4px 10px rgba(26,115,232,.3)" : "none",
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{s === 2 ? "1+" : s}</div>
                  <div style={{ fontSize: 9.5 }}>stop</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={S.filterSec}>
        <div style={S.filterSecHead} onClick={() => toggleSec("time")}>
          <span style={S.filterTitle}><IconClock /> Departure time</span>
          <span style={{ ...S.chevron, transform: openSecs.time ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
        </div>
        {openSecs.time && [
          ["Early morning", "12–6 AM", 1],
          ["Morning", "6–12 PM", 2],
          ["Afternoon", "12–6 PM", 3],
          ["Evening", "6–12 AM", 4],
        ].map(([lbl, range, slot]) => {
          const active = filters.depSlots.includes(slot);
          return (
            <label
              key={slot}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "7px 8px", fontSize: 12.5, cursor: "pointer", borderRadius: 8,
                background: active ? "#F0F4FF" : "transparent", marginBottom: 2, transition: "background .15s ease",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggle("depSlots", slot)}
                  style={{ accentColor: "#1a73e8", width: 14, height: 14 }}
                />
                <span style={{ fontWeight: active ? 600 : 500, color: active ? "#0F1B33" : "#3a3d4a" }}>{lbl}</span>
              </span>
              <span style={{ fontSize: 10.5, color: "#6B7385" }}>{range}</span>
            </label>
          );
        })}
      </div>

      <div style={S.filterSec}>
        <div style={S.filterSecHead} onClick={() => toggleSec("airlines")}>
          <span style={S.filterTitle}><IconAirlineTag /> Airlines</span>
          <span style={{ ...S.chevron, transform: openSecs.airlines ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
        </div>
        {openSecs.airlines && availableAirlines.map((a) => {
          const active = filters.airlines.includes(a.code);
          return (
            <label
              key={a.code}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "7px 8px", fontSize: 12.5, cursor: "pointer", borderRadius: 8,
                background: active ? "#F0F4FF" : "transparent", marginBottom: 2, transition: "background .15s ease",
              }}
            >
              <div style={{ ...S.logoBox, background: "#fff", border: "1px solid #F0F1F5" }}>
                <img
                  src={`/Flight_Images/${a.code}.gif`}
                  alt={a.code}
                  style={S.logoImg}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </div>
              <input
                type="checkbox"
                checked={active}
                onChange={() => toggle("airlines", a.code)}
                style={{ accentColor: "#1a73e8", width: 14, height: 14 }}
              />
              <span style={{ fontWeight: active ? 600 : 500, color: active ? "#0F1B33" : "#3a3d4a" }}>
                {a.name} <span style={{ color: "#6B7385", fontWeight: 500 }}>({a.code})</span>
              </span>
            </label>
          );
        })}
      </div>

      <div style={{ ...S.filterSec, borderBottom: "none" }}>
        <div style={S.filterSecHead} onClick={() => toggleSec("refundable")}>
          <span style={S.filterTitle}>Refundable</span>
          <span style={{ ...S.chevron, transform: openSecs.refundable ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
        </div>

        {openSecs.refundable && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[
              { label: "Refundable", value: true },
              { label: "Non-Refundable", value: false },
            ].map(({ label, value }) => {
              const active = filters.refundable.includes(value);
              return (
                <label
                  key={String(value)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", fontSize: 12.5,
                    cursor: "pointer", borderRadius: 8, background: active ? "#F0F4FF" : "transparent",
                    marginBottom: 2, transition: "background .15s ease",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggle("refundable", value)}
                    style={{ accentColor: "#1a73e8", width: 14, height: 14 }}
                  />
                  <span style={{ fontWeight: active ? 600 : 500, color: active ? "#0F1B33" : "#3a3d4a" }}>
                    {label}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const fdS = {
  wrap: { background: "#EEF2F7", padding: "8px 12px 12px", borderTop: "1px solid #F0F1F5" },
  title: { fontSize: 13, color: "#1a73e8", fontWeight: 500, borderBottom: "1px solid #1a73e8", display: "inline-block", marginBottom: 8 },
  tabBar: { display: "flex", alignItems: "center", background: "#F3F4F6", borderRadius: 30, padding: 4, marginBottom: 12, gap: 4 },
  tab: { flex: 1, textAlign: "center", padding: "10px 8px", fontSize: 13, fontWeight: 600, color: "#0F1B33", borderRadius: 30, cursor: "pointer" },
  tabActive: { flex: 1, textAlign: "center", padding: "10px 8px", fontSize: 13, fontWeight: 600, color: "#fff", background: "#2196F3", borderRadius: 30, cursor: "pointer" },
  closeBtn: { width: 24, height: 24, borderRadius: "50%", border: "none", background: "#C7C9D1", color: "#fff", cursor: "pointer", fontSize: 13, marginLeft: 6, flexShrink: 0 },
  body: { background: "#fff", borderRadius: 12, padding: "14px 18px" },
  route: { fontSize: 13, fontWeight: 500, color: "#0F1B33", marginBottom: 8 },
  segRow: { display: "grid", gridTemplateColumns: "1.3fr 1fr 0.8fr 1fr", gap: 10, alignItems: "start", marginBottom: 6 },
  bigTime: { fontSize: 20, fontWeight: 700, color: "#0F1B33", marginBottom: 4 },
  small: { fontSize: 12, color: "#4A5670", lineHeight: 1.5 },
  cityBold: { fontSize: 12.5, fontWeight: 700, color: "#3a3d4a", marginBottom: 2 },
  layoverLine: { position: "relative", textAlign: "center", margin: "16px 0", borderTop: "1px dashed #C7CEDB" },
  layoverPill: { position: "relative", top: -11, background: "#EEF2FF", border: "1px solid #C7D2F5", borderRadius: 20, padding: "3px 14px", fontSize: 11.5, color: "#0F1B33", display: "inline-block" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  td: { padding: "8px 4px", borderBottom: "1px solid #F0F1F5", color: "#3a3d4a" },
  ruleNote: { fontSize: 12, color: "#4A5670", background: "#FFF8E1", border: "1px solid #FFE0A3", borderRadius: 8, padding: "8px 12px", marginBottom: 12 },
  ruleTableWrap: { overflowX: "auto", border: "1px solid #E8EAF0", borderRadius: 10 },
  ruleTable: { width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 640 },
  ruleTh: { textAlign: "left", padding: "10px 12px", background: "#F3F6FB", color: "#14213D", fontWeight: 700, fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".4px", borderBottom: "1px solid #E8EAF0", whiteSpace: "nowrap" },
  ruleTd: { padding: "12px", borderBottom: "1px solid #F0F1F5", color: "#3a3d4a", verticalAlign: "middle" },
  fareNameCell: { fontWeight: 700, color: "#0F1B33", fontSize: 13 },
  fareSubCell: { fontSize: 11, color: "#6B7385", marginTop: 2 },
  naText: { color: "#6B7385", fontStyle: "italic", fontSize: 12 },
};

const FD_TABS = ["Flight Information", "Fare Details & Rules", "Baggage Information", "Cancellation & Change Rule"];

const segDuration = (seg) => {
  const m = diffMinutes(seg?.DepartureDateTime, seg?.ArrivalDateTime);
  // NOTE: agar timezone alag ho to diff galat aa sakta hai, isliye API Duration ko priority
  return seg?.Duration ? formatDur(seg.Duration) : (m !== null ? formatLayover(m) : "N/A");
};

function SegmentBlock({ seg, cabin, index }) {
  const lc = LOGO_COLORS[index % LOGO_COLORS.length];
  return (
    <>
      <div style={fdS.route}>{seg.Origen} → {seg.Destination}</div>
      <div style={fdS.segRow}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <AirlineLogo airlineCode={seg.AirlineCode} bgColor={lc} textColor={lc} initials={(seg.AirlineCode || "?").substring(0, 2)} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0F1B33" }}>{seg.AirlineName}</div>
            <div style={fdS.small}>{seg.AirlineCode}-{seg.FlightNumber}</div>
            <div style={fdS.small}>{cabin}</div>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={fdS.bigTime}>{formatTime(seg.DepartureDateTime)}</div>
          <div style={fdS.cityBold}>{seg.SourceCity} ({seg.Origen})</div>
          <div style={fdS.small}>{formatDateShort(seg.DepartureDateTime)}</div>
          {/* Field names apne API ke hisaab se check kar lena */}
          {seg.OriginAirportName && <div style={fdS.small}>{seg.OriginAirportName}</div>}
          {seg.OriginTerminal && <div style={fdS.small}>Terminal - {seg.OriginTerminal}</div>}
        </div>

        <div style={{ textAlign: "center", paddingTop: 22 }}>
          <div style={{ fontSize: 15 }}>⏰</div>
          <div style={{ fontSize: 13, color: "#3a3d4a" }}>{segDuration(seg)}</div>
        </div>

        <div style={{ textAlign: "left" }}>
          <div style={fdS.bigTime}>{formatTime(seg.ArrivalDateTime)}</div>
          <div style={fdS.cityBold}>{seg.DestinationCity} ({seg.Destination})</div>
          <div style={fdS.small}>{formatDateShort(seg.ArrivalDateTime)}</div>
          {seg.DestinationAirportName && <div style={fdS.small}>{seg.DestinationAirportName}</div>}
          {seg.DestinationTerminal && <div style={fdS.small}>Terminal - {seg.DestinationTerminal}</div>}
        </div>
      </div>
    </>
  );
}

function LegSegments({ segs, cabin }) {
  return segs.map((seg, i) => {
    const next = segs[i + 1];
    const mins = next ? diffMinutes(seg.ArrivalDateTime, next.DepartureDateTime) : null;
    return (
      <div key={i}>
        <SegmentBlock seg={seg} cabin={cabin} index={i} />
        {next && (
          <div style={fdS.layoverLine}>
            <span style={fdS.layoverPill}>
              Change of planes | <b>{mins !== null ? `${Math.floor(mins / 60)}h :${mins % 60}m` : "N/A"}</b> Layover in {seg.DestinationCity || seg.Destination}
            </span>
          </div>
        )}
      </div>
    );
  });
}
function CancellationRules({ fareGroups, refundable }) {
  const val = (v) => {
    if (v === null || v === undefined || v === "" || String(v).toUpperCase() === "NA") {
      return <span style={fdS.naText}>As per airline rules</span>;
    }
    return <span>{v}</span>;
  };

  if (!fareGroups || fareGroups.length === 0) {
    return (
      <div style={fdS.ruleNote}>
        Cancellation & change charges will be as per the airline's rules.
        &nbsp;<span style={refundable ? S.badgeGreen : S.badgeAmber}>{refundable ? "✓ Refundable" : "✕ Non-Refundable"}</span>
      </div>
    );
  }

  return (
    <>
      <div style={fdS.ruleNote}>
        Cancellation & change charges will be as per the airline's rules.
        &nbsp;<span style={refundable ? S.badgeGreen : S.badgeAmber}>{refundable ? "✓ Refundable" : "✕ Non-Refundable"}</span>
      </div>

      <div style={fdS.ruleTableWrap}>
        <table style={fdS.ruleTable}>
          <thead>
            <tr>
              {["Fare Type", "Refund", "Cancellation", "Date Change", "Seat", "Meal", "Fare"].map((h, i) => (
                <th key={h} style={{ ...fdS.ruleTh, textAlign: i === 6 ? "right" : "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fareGroups.map((fg, i) => (
              <tr key={fg.ContractId || i} style={{ background: i % 2 ? "#FAFBFF" : "#fff" }}>
                <td style={fdS.ruleTd}>
                  <div style={fdS.fareNameCell}>
                    {(fg.FareName || "Fare").replace(/_/g, " ")}
                  </div>
                  <div style={fdS.fareSubCell}>
                    🧳 {fg.CheckInBaggage || "N/A"} · 💼 {fg.HandBaggage || "N/A"}
                  </div>
                </td>
                <td style={fdS.ruleTd}>
                  <span style={fg.IsRefundable ? S.badgeGreen : S.badgeAmber}>
                    {fg.IsRefundable ? "✓ Refundable" : "✕ Non-Refundable"}
                  </span>
                </td>
                <td style={fdS.ruleTd}>{val(fg.CancelCharge)}</td>
                <td style={fdS.ruleTd}>{val(fg.DateChange)}</td>
                <td style={fdS.ruleTd}>{val(fg.SeatCharge)}</td>
                <td style={fdS.ruleTd}>{val(fg.MealCharge)}</td>
                <td style={{ ...fdS.ruleTd, textAlign: "right", fontWeight: 700, color: "#0F1B33" }}>
                  ₹{Math.round(fg.GrossFare || 0).toLocaleString("en-IN")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
function FlightDetailsPanel({ flight, returnFlight, cabin, onClose }) {
  const [tab, setTab] = useState(0);
  const d = getDisplayData(flight);
  const r = returnFlight ? getDisplayData(returnFlight) : null;

  const allSegs = flight?.AirSegments || [];
  const onwardSegs = allSegs.filter((s) => isOnwardT(s.SegmentType));
  const ownReturnSegs = allSegs.filter((s) => isReturnT(s.SegmentType));
  const extraReturnSegs = (returnFlight?.AirSegments || []).filter((s) => isReturnT(s.SegmentType));

  const legs = [
    { label: "Onward", segs: onwardSegs.length ? onwardSegs : allSegs, d, bag: [d.handBag, d.baggage] },
    ...(ownReturnSegs.length && onwardSegs.length
      ? [{ label: "Return", segs: ownReturnSegs, d, bag: [d.retHandBag, d.retBaggage] }]
      : extraReturnSegs.length
        ? [{ label: "Return", segs: extraReturnSegs, d: r, bag: [r?.retHandBag, r?.retBaggage] }]
        : []),
  ];

  const money = (v) => `₹${Math.round(v || 0).toLocaleString("en-IN")}`;

  return (
    <div style={fdS.wrap}>
      <div style={fdS.title}>Flight Details</div>

      <div style={fdS.tabBar}>
        {FD_TABS.map((t, i) => (
          <div key={t} style={tab === i ? fdS.tabActive : fdS.tab} onClick={() => setTab(i)}>{t}</div>
        ))}
        <button style={fdS.closeBtn} onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div style={fdS.body}>
        {tab === 0 && legs.map((leg, i) => (
          <div key={i} style={i > 0 ? { marginTop: 18, paddingTop: 14, borderTop: "1px solid #E8EAF0" } : {}}>
            <LegSegments segs={leg.segs} cabin={cabin} />
          </div>
        ))}

        {tab === 1 && (
          <table style={fdS.table}>
            <tbody>
              <tr><td style={fdS.td}>Base Fare</td><td style={{ ...fdS.td, textAlign: "right" }}>{money(d.baseFare)}</td></tr>
              <tr><td style={fdS.td}>Taxes & Fees</td><td style={{ ...fdS.td, textAlign: "right" }}>{money(d.tax)}</td></tr>
              {r && <tr><td style={fdS.td}>Return Fare</td><td style={{ ...fdS.td, textAlign: "right" }}>{money(r.price)}</td></tr>}
              <tr><td style={{ ...fdS.td, fontWeight: 700 }}>Total (per person)</td><td style={{ ...fdS.td, textAlign: "right", fontWeight: 700 }}>{money(d.price + (r ? r.price : 0))}</td></tr>
              <tr><td style={fdS.td}>Net Fare (TDS extra)</td><td style={{ ...fdS.td, textAlign: "right" }}>{money(d.netFare)}</td></tr>
              <tr><td style={fdS.td}>Refundable</td><td style={{ ...fdS.td, textAlign: "right" }}>{d.refundable ? "Yes" : "No"}</td></tr>
            </tbody>
          </table>
        )}

        {tab === 2 && (
          <table style={fdS.table}>
            <thead>
              <tr>{["Sector", "Cabin Baggage", "Check-in Baggage"].map((h) => (
                <th key={h} style={{ ...fdS.td, textAlign: "left", fontWeight: 700 }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {legs.map((leg, i) => (
                <tr key={i}>
                  <td style={fdS.td}>{leg.label}: {leg.segs[0]?.Origen} → {leg.segs[leg.segs.length - 1]?.Destination}</td>
                  <td style={fdS.td}>{leg.bag[0] || "N/A"}</td>
                  <td style={fdS.td}>{leg.bag[1] || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 3 && (
          <CancellationRules fareGroups={d.fareGroups} refundable={d.refundable} />
        )}
      </div>
    </div>
  );
}

// ====================== FARE SELECT MODAL ======================
// Contract ke fare options. FareGroups na ho to default ek fare bana do
const getFareOptions = (flight) => {
  const groups = flight?.FareGroups?.FareGroup || [];
  if (groups.length) return groups;
  const d = getDisplayData(flight);
  return [{
    ContractId: flight?.ContractId,
    FareName: "Standard",
    CheckInBaggage: d.baggage,
    HandBaggage: d.handBag,
    IsRefundable: d.refundable,
    GrossFare: d.price,
  }];
};

const FARE_HEAD_H = 176;
const FARE_ROW_H = 48;

const fmS = {
  overlay: { position: "fixed", inset: 0, background: "rgba(11,24,52,.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  box: { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 1280, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(8,18,45,.4)", overflow: "hidden", fontFamily: "'Manrope', 'Segoe UI', system-ui, sans-serif", color: "#0F1B33" },
  head: { padding: "22px 28px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: { fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: "-.3px" },
  closeBtn: { width: 36, height: 36, borderRadius: "50%", border: "none", background: "transparent", color: "#3B4663", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  tabs: { display: "flex", gap: 8, padding: "0 28px 14px", flexWrap: "wrap" },
  tab: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, padding: "8px 16px", borderRadius: 10, border: "1.5px solid #E3E7EF", background: "#fff", cursor: "pointer", textAlign: "left" },
  tabActive: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, padding: "8px 16px", borderRadius: 10, border: "1.5px solid #1a73e8", background: "#F5F9FF", cursor: "pointer", textAlign: "left" },
  tabTop: { fontSize: 13, fontWeight: 800 },
  tabSub: { fontSize: 11.5, color: "#6B7385", fontWeight: 600 },
  summary: { display: "flex", alignItems: "center", gap: 18, padding: "14px 28px", borderTop: "1px solid #E8EAF0", borderBottom: "1px solid #E8EAF0", background: "#fff" },
  summaryLogo: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 76, flexShrink: 0, fontSize: 11.5, fontWeight: 600, textAlign: "center" },
  summaryRoute: { fontSize: 14, color: "#6B7385", fontWeight: 600 },
  summaryTime: { fontSize: 18, fontWeight: 800, marginTop: 2, letterSpacing: "-.2px" },
  scroll: { overflowX: "hidden", overflowY: "auto", padding: "24px 28px", background: "#fff", flex: 1 },
  labelCol: { paddingTop: 2 },
  labelCell: { height: FARE_ROW_H, display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 700, color: "#0F1B33", boxSizing: "border-box" },
  col: { border: "2px solid #E3E7EF", borderRadius: 14, background: "#fff", overflow: "hidden", display: "flex", flexDirection: "column", cursor: "pointer", transition: "border-color .15s ease, box-shadow .15s ease", boxSizing: "border-box" },
  colActive: { border: "2px solid #1a73e8", borderRadius: 14, background: "linear-gradient(180deg, #EEF5FF 0%, #fff 55%)", overflow: "hidden", display: "flex", flexDirection: "column", cursor: "pointer", boxShadow: "0 0 0 3px rgba(26,115,232,.12)", boxSizing: "border-box" },
  colHead: { height: FARE_HEAD_H, padding: "18px 14px 16px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxSizing: "border-box" },
  fareName: { fontSize: 14.5, fontWeight: 700, color: "#3B4663", lineHeight: 1.25 },
  farePrice: { fontSize: 26, fontWeight: 800, letterSpacing: "-.5px", marginTop: 2 },
  cell: { height: FARE_ROW_H, padding: "0 14px", display: "flex", alignItems: "center", fontSize: 13, lineHeight: 1.3, color: "#0F1B33", borderTop: "1px dashed #DDE2EC", boxSizing: "border-box", overflow: "hidden" },
  cellMuted: { color: "#6B7385" },
  selectBtn: { width: "100%", height: 42, borderRadius: 8, border: "1.5px solid #0F1B33", background: "#fff", color: "#0F1B33", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 },
  selectBtnOn: { width: "100%", height: 42, borderRadius: 8, border: "1.5px solid #0F1B33", background: "#0F1B33", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 },
  foot: { padding: "16px 28px", borderTop: "1px solid #E8EAF0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, background: "#fff", flexWrap: "wrap" },
  footNote: { fontSize: 12.5, color: "#6B7385", fontWeight: 600, flex: 1, minWidth: 180 },
  footTotalLabel: { fontSize: 12, color: "#6B7385", textAlign: "right" },
  footTotal: { fontSize: 26, fontWeight: 800, letterSpacing: "-.4px", textAlign: "right" },
  continueBtn: { background: "#E5246F", color: "#fff", border: "none", borderRadius: 10, padding: "0 36px", height: 50, fontSize: 15, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" },
};

const FARE_ROWS = [
  { key: "CancelCharge",   label: "Cancellation fee",     icon: "cancel",  kind: "charge" },
  { key: "DateChange",     label: "Date change fee",      icon: "change",  kind: "charge" },
  { key: "SeatCharge",     label: "Seat selection",       icon: "seat",    kind: "charge" },
  { key: "MealCharge",     label: "Meal selection",       icon: "meal",    kind: "charge" },
  { key: "HandBaggage",    label: "Cabin bag / adult",    icon: "cabin",   kind: "bag" },
  { key: "CheckInBaggage", label: "Check-in bag / adult", icon: "checkin", kind: "bag" },
];

// Raw API value -> readable text. Numbers become rupee amounts.
const fareCellValue = (row, v) => {
  const empty = v === null || v === undefined || v === "" || String(v).toUpperCase() === "NA" || String(v).toUpperCase() === "N/A";
  if (empty) return { text: row.kind === "bag" ? "N/A" : "As per airline rules", muted: true };
  if (/^\d+(\.\d+)?$/.test(String(v).trim())) {
    const n = Number(v);
    return { text: row.kind === "charge" && n === 0 ? "Free" : `₹${n.toLocaleString("en-IN")}`, muted: false };
  }
  return { text: String(v), muted: false };
};

const titleCase = (s) => String(s || "Fare").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const IconX = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

function FareRowIcon({ name }) {
  const paths = {
    cancel:  <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18M10 14l4 4M14 14l-4 4" /></>,
    change:  <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18M8 15h8" /></>,
    seat:    <><path d="M8 3h3v10h6l2 7M8 13l-2 7" /></>,
    meal:    <><path d="M6 3v7a2 2 0 0 0 2 2v9M10 3v7M8 3v7M17 21V3c-2 1-3 4-3 8h3" /></>,
    cabin:   <><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M9 8V6a3 3 0 0 1 6 0v2" /></>,
    checkin: <><rect x="6" y="6" width="12" height="15" rx="2" /><path d="M9 6V3h6v3M9 11v6M15 11v6" /></>,
  };
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#6B7385" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

const getLegInfo = (flight, isRet) => {
  const d = getDisplayData(flight);
  const useRet = !!(isRet && d.retOrigin);
  const stops = useRet ? d.retStops : d.stops;
  return {
    airline: d.airline,
    airlineCode: useRet ? d.retAirlineCode : d.airlineCode,
    code: useRet ? d.retCode : d.code,
    from: useRet ? (d.retOriginCity || d.retOrigin) : (d.originCity || d.origin),
    to: useRet ? (d.retDestCity || d.retDest) : (d.destCity || d.dest),
    fromCode: useRet ? d.retOrigin : d.origin,
    toCode: useRet ? d.retDest : d.dest,
    date: formatDateShort(useRet ? d.retDepDate : d.depDate).replace(/\s\d{4}$/, ""),
    dep: useRet ? d.retDep : d.dep,
    arr: useRet ? d.retArr : d.arr,
    dur: formatDur(useRet ? d.retDurMin : d.durMin),
    stopsText: stops === 0 ? "Non-stop" : `${stops} stop${stops > 1 ? "s" : ""}`,
  };
};

function FareSelectModal({ legs, onClose, onConfirm }) {
  // legs: [{ label: "Onward", flight }, { label: "Return", flight }]
  const [picked, setPicked] = useState(() => legs.map(() => 0));
  const [active, setActive] = useState(0);
  const optionsPerLeg = useMemo(() => legs.map((l) => getFareOptions(l.flight)), [legs]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isLast = active === legs.length - 1;
  const leg = legs[active];
  const options = optionsPerLeg[active];
  const info = getLegInfo(leg.flight, leg.label === "Return");
  const money = (v) => `₹${Math.round(v || 0).toLocaleString("en-IN")}`;

  const total = optionsPerLeg.reduce((sum, opts, i) => sum + (opts[picked[i]]?.GrossFare || 0), 0);
  const choose = (fi) => setPicked((p) => p.map((v, k) => (k === active ? fi : v)));

  return (
    <div style={fmS.overlay} onClick={onClose}>
      <div style={fmS.box} role="dialog" aria-modal="true" aria-label="Choose your fare" onClick={(e) => e.stopPropagation()}>
        <div style={fmS.head}>
          <h2 style={fmS.title}>Select your fare</h2>
          <button style={fmS.closeBtn} onClick={onClose} aria-label="Close"><IconX /></button>
        </div>

        {legs.length > 1 && (
          <div style={fmS.tabs} role="tablist">
            {legs.map((l, i) => {
              const li = getLegInfo(l.flight, l.label === "Return");
              const chosen = optionsPerLeg[i][picked[i]];
              return (
                <button key={l.label} role="tab" aria-selected={active === i} style={active === i ? fmS.tabActive : fmS.tab} onClick={() => setActive(i)}>
                  <span style={fmS.tabTop}>{l.label}: {li.fromCode} → {li.toCode}</span>
                  <span style={fmS.tabSub}>{titleCase(chosen?.FareName)} · {money(chosen?.GrossFare)}</span>
                </button>
              );
            })}
          </div>
        )}

        <div style={fmS.summary}>
          <div style={fmS.summaryLogo}>
            <AirlineLogo airlineCode={info.airlineCode} bgColor="#1a73e8" textColor="#1a73e8" initials={(info.airlineCode || "?").substring(0, 2)} />
            <span>{info.airline}</span>
          </div>
          <div>
            <div style={fmS.summaryRoute}>{info.from} → {info.to}{info.date ? ` · ${info.date}` : ""}</div>
            <div style={fmS.summaryTime}>{info.dep} - {info.arr} · {info.dur} ({info.stopsText})</div>
          </div>
        </div>

        <div style={fmS.scroll}>
          <div style={{ display: "grid", gridTemplateColumns: `170px repeat(${options.length}, minmax(0, 1fr))`, gap: options.length > 4 ? 12 : 16 }}>
            <div style={fmS.labelCol}>
              <div style={{ height: FARE_HEAD_H }} />
              {FARE_ROWS.map((row) => (
                <div key={row.key} style={fmS.labelCell}><FareRowIcon name={row.icon} />{row.label}</div>
              ))}
            </div>

            {options.map((fg, fi) => {
              const on = picked[active] === fi;
              return (
                <div key={fg.ContractId || fi} style={on ? fmS.colActive : fmS.col} onClick={() => choose(fi)}>
                  <div style={fmS.colHead}>
                    <div>
                      <div style={fmS.fareName}>{titleCase(fg.FareName)}</div>
                      <div style={fmS.farePrice}>{money(fg.GrossFare)}</div>
                      <div style={{ marginTop: 6 }}>
                        <span style={fg.IsRefundable ? S.badgeGreen : S.badgeAmber}>
                          {fg.IsRefundable ? "✓ Refundable" : "✕ Non-Refundable"}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      style={on ? fmS.selectBtnOn : fmS.selectBtn}
                      onClick={(e) => { e.stopPropagation(); choose(fi); }}
                    >
                      {on ? <><IconCheck /> Selected</> : "Select"}
                    </button>
                  </div>
                  {FARE_ROWS.map((row) => {
                    const c = fareCellValue(row, fg[row.key]);
                    return (
                      <div key={row.key} style={{ ...fmS.cell, ...(c.muted ? fmS.cellMuted : null) }} title={c.text}>
                        {c.text}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div style={fmS.foot}>
          <div style={fmS.footNote}>
            {legs.length > 1
              ? legs.map((l, i) => `${l.label}: ${titleCase(optionsPerLeg[i][picked[i]]?.FareName)}`).join("  ·  ")
              : "Fare per person, taxes included"}
          </div>
          <div>
            <div style={fmS.footTotalLabel}>{legs.length > 1 ? "Total per person" : "Fare per person"}</div>
            <div style={fmS.footTotal}>{money(total)}</div>
          </div>
          <button
            style={fmS.continueBtn}
            onClick={() => (isLast ? onConfirm(optionsPerLeg.map((opts, i) => opts[picked[i]])) : setActive(active + 1))}
          >
            {isLast ? "Continue" : `Choose ${legs[active + 1].label.toLowerCase()} fare`}
          </button>
        </div>
      </div>
    </div>
  );
}

function FlightCard({ flight, returnFlight, index, sessionData, isDomestic, searchData, passengersArray, showTds, allContracts }) {
  const [expanded, setExpanded] = useState(false);
  const [showFares, setShowFares] = useState(false);
  const navigate = useNavigate();
  const d = getDisplayData(flight);
  const lockFee = Math.min(
    999,
    Math.max(199, Math.round((d.price || 0) * 0.05))
  );
  // FIX: Combined (international) round trip me returnFlight nahi hota,
  // return legs usi contract ke andar hote hain. Isliye d se hi return row dikhao.
  const combinedRT = !returnFlight && d.isRoundTrip;
  const r = returnFlight ? getDisplayData(returnFlight) : (combinedRT ? d : null);
  const lc = LOGO_COLORS[index % LOGO_COLORS.length];
  return (
    <div style={S.flightCard} className="fc-card">
      <div style={S.flightRow}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AirlineLogo airlineCode={d.airlineCode} bgColor={lc} textColor={lc} initials={d.logo} />
          <div>
            <span style={{ ...S.tripLabel, background: "#e8f0fe", color: "#1a73e8" }}>✈ ONWARD</span>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0F1B33" }}>{d.airline}</div>
            <div style={{ fontSize: 11, color: "#6B7385" }}>{d.code}</div>
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={S.timeBig}>{d.dep}</div>
          <div style={S.timeSub}>{d.origin}</div>
          {d.originCity && <div style={{ fontSize: 10, color: "#6B7385" }}>{d.originCity}</div>}
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#6B7385" }}>{formatDur(d.durMin)}</div>
          <RouteLine />
          <span
            style={d.stops === 0 ? S.badgeGreen : S.badgeAmber}
            title={
              d.stops > 0 && d.layovers.length > 0
                ? d.layovers.map((l) => `${l.airportCode}${l.durationLabel ? ` · ${l.durationLabel} layover` : ""}`).join(", ")
                : undefined
            }
          >
            {d.stops === 0
              ? "Non-stop"
              : d.layovers.length > 0
                ? `${d.stops} stop${d.stops > 1 ? "s" : ""} · ${d.layovers.map((l) => `${l.airportCode}${l.durationLabel ? ` (${l.durationLabel})` : ""}`).join(", ")}`
                : `${d.stops} stop${d.stops > 1 ? "s" : ""}`}
          </span>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={S.timeBig}>{d.arr}</div>
          <div style={S.timeSub}>{d.dest}</div>
          {d.destCity && <div style={{ fontSize: 10, color: "#6B7385" }}>{d.destCity}</div>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={S.priceSub}>{combinedRT ? "per person (round trip)" : "per person"}</div>
          <span style={d.refundable ? S.badgeGreen : S.badgeAmber}>
            {d.refundable ? "✓ Refundable" : "✕ Non-Refundable"}
          </span>
          {/* Amount ke side me TDS (TDS button on ho tabhi) */}
          <div style={S.priceRow}>
            <div style={S.priceAmt}>₹{Math.round(d.price).toLocaleString("en-IN")}</div>
            {showTds && <TdsBadge amount={d.tdsAmount} />}
          </div>

          {/* Fare actions — Book upar, Lock neeche */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 11,
              marginTop: 6,
              textAlign: "right",
            }}
          >
            <button
              className="btn-primary"
              style={{ ...S.bookBtn, width: 118, marginTop: 0 }}
              onClick={() => setShowFares(true)}
            >
              Book now
            </button>

            <button
              type="button"
              className="btn-lock"
              style={S.lockPriceBtn}
              onClick={(e) => {
                e.stopPropagation();
                alert(`Lock this fare for ₹${lockFee}`);
              }}
              title="Lock this price for a small fee"
            >
              🔒 Lock Price ₹{lockFee}
            </button>
          </div>
        </div>
      </div>

      {r && (
        <div style={S.returnRow}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AirlineLogo airlineCode={r.retAirlineCode} bgColor={lc} textColor={lc} initials={r.logo} />
            <div>
              <span style={{ ...S.tripLabel, background: "#fce4ec", color: "#c2185b" }}>↩ RETURN</span>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F1B33" }}>{r.airline}</div>
              <div style={{ fontSize: 11, color: "#6B7385" }}>{r.retCode}</div>
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={S.timeBig}>{r.retDep}</div>
            <div style={S.timeSub}>{r.retOrigin}</div>
            {r.retOriginCity && <div style={{ fontSize: 10, color: "#6B7385" }}>{r.retOriginCity}</div>}
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#6B7385" }}>{formatDur(r.retDurMin)}</div>
            <RouteLine />
            <span
              style={r.retStops === 0 ? S.badgeGreen : S.badgeAmber}
              title={
                r.retStops > 0 && r.retLayovers.length > 0
                  ? r.retLayovers.map((l) => `${l.airportCode}${l.durationLabel ? ` · ${l.durationLabel} layover` : ""}`).join(", ")
                  : undefined
              }
            >
              {r.retStops === 0
                ? "Non-stop"
                : r.retLayovers.length > 0
                  ? `${r.retStops} stop${r.retStops > 1 ? "s" : ""} · ${r.retLayovers.map((l) => `${l.airportCode}${l.durationLabel ? ` (${l.durationLabel})` : ""}`).join(", ")}`
                  : `${r.retStops} stop${r.retStops > 1 ? "s" : ""}`}
            </span>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={S.timeBig}>{r.retArr}</div>
            <div style={S.timeSub}>{r.retDest}</div>
            {r.retDestCity && <div style={{ fontSize: 10, color: "#6B7385" }}>{r.retDestCity}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            {combinedRT ? (
              <div style={{ fontSize: 11, color: "#4A5670", fontWeight: 500 }}>Included in total fare</div>
            ) : (
              <>
                <div style={{ fontSize: 11, color: "#4A5670", fontWeight: 500 }}>Return fare</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#0F1B33" }}>₹{Math.round(r.price).toLocaleString("en-IN")}</div>
              </>
            )}
          </div>
        </div>
      )}

      <div style={S.detailBar}>
        <span style={S.badgeGray}>💼 Cabin: {d.handBag} | 🧳 Checked: {d.baggage}</span>
        {d.isRoundTrip && <span style={{ ...S.badgeGray, background: "#fce4ec", color: "#c2185b" }}>Round Trip</span>}
        <span
          onClick={() => setExpanded(v => !v)}
          style={{ marginLeft: "auto", fontSize: 11, color: "#1a73e8", cursor: "pointer", fontWeight: 500 }}
        >
          {expanded ? "▲ Hide details" : "▼ Flight details"}
        </span>
      </div>

      {expanded && (
        <FlightDetailsPanel
          flight={flight}
          returnFlight={returnFlight}
          cabin={CLASS_NAMES[searchData?.TypeOfClass] ?? "Economy"}
          onClose={() => setExpanded(false)}
        />
      )}

      {showFares && (
        <FareSelectModal
          legs={[
            { label: "Onward", flight },
            ...(returnFlight && returnFlight !== flight ? [{ label: "Return", flight: returnFlight }] : []),
          ]}
          onClose={() => setShowFares(false)}
          onConfirm={(fares) => {
            setShowFares(false);
            const selFlight = applySelectedFare(flight, fares[0], allContracts);
            const selReturn = returnFlight && returnFlight !== flight
              ? applySelectedFare(returnFlight, fares[1], allContracts)
              : selFlight;

            navigate("/FareDetails", {
              state: {
                flight: selFlight,
                returnFlight: selReturn,
                selectedFare: fares[0],
                selectedReturnFare: fares[1] || null,
                allContracts,
                apiToken: selFlight?._apiToken ?? sessionData?.apiToken,
                bookingKey: selFlight?._bookingKey ?? sessionData?.bookingKey,
                returnApiToken: selReturn?._apiToken ?? sessionData?.apiToken,
                returnBookingKey: selReturn?._bookingKey ?? sessionData?.bookingKey,
                agencyKey: sessionData?.agencyKey,
                IsDomestic: isDomestic,
                passengers: passengersArray,
                AdultCount: searchData?.AdultCount || 1,
                ChildCount: searchData?.ChildCount || 0,
                InfantCount: searchData?.InfantCount || 0,
              },
            });
          }}
        />
      )}
    </div>
  );
}

// `combined` = international combined fare ko split view me dikhana (ek contract = onward + return).
// Is case me price poore round trip ka hota hai.
function OnwardLegCard({ flight, index, isSelected, onSelect, showTds, combined }) {
  const d = getDisplayData(flight);
  const lc = LOGO_COLORS[index % LOGO_COLORS.length];

  return (
    <div
      style={isSelected ? S.legCardSelected : S.legCard}
      className={`split-leg-card split-onward-card${isSelected ? " selected" : ""}`}
      onClick={onSelect}
    >
      <div style={S.legRow}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AirlineLogo airlineCode={d.airlineCode} bgColor={lc} textColor={lc} initials={d.logo} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0F1B33" }}>{d.airline}</div>
            <div style={{ fontSize: 11, color: "#6B7385" }}>{d.code}</div>
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={S.timeBig}>{d.dep}</div>
          <div style={S.timeSub}>{d.origin}</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#6B7385" }}>{formatDur(d.durMin)}</div>
          <span
            style={d.stops === 0 ? S.badgeGreen : S.badgeAmber}
            title={
              d.stops > 0 && d.layovers.length > 0
                ? d.layovers.map((l) => `${l.airportCode}${l.durationLabel ? ` · ${l.durationLabel} layover` : ""}`).join(", ")
                : undefined
            }
          >
            {d.stops === 0
              ? "Non-stop"
              : d.layovers.length > 0
                ? `${d.stops} stop${d.stops > 1 ? "s" : ""} · ${d.layovers.map((l) => l.airportCode).join(", ")}`
                : `${d.stops} stop${d.stops > 1 ? "s" : ""}`}
          </span>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={S.timeBig}>{d.arr}</div>
          <div style={S.timeSub}>{d.dest}</div>
        </div>
      </div>

      <div style={S.legBookRow}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={d.refundable ? S.badgeGreen : S.badgeAmber}>
            {d.refundable ? "✓ Refundable" : "✕ Non-Refundable"}
          </span>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            {combined && <span style={{ fontSize: 10.5, color: "#6B7385" }}>Round trip total</span>}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={S.priceAmt}>₹{Math.round(d.price).toLocaleString("en-IN")}</span>
              {showTds && <TdsBadge amount={d.tdsAmount} />}
            </div>
          </div>
        </div>
        <button
          style={isSelected ? S.selectBtnActive : S.selectBtn}
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
        >
          {isSelected ? <><IconCheck /> Selected</> : "Select"}
        </button>
      </div>
    </div>
  );
}

function ReturnLegCard({ flight, index, isSelected, onSelect, showTds, combined }) {
  const d = getDisplayData(flight);
  const lc = LOGO_COLORS[index % LOGO_COLORS.length];

  // Ab isPureReturn bhi allow karo
  if (!d.isRoundTrip && !d.isPureReturn) {
    return (
      <div style={{ ...S.legCard, padding: "14px", color: "#6B7385", fontSize: 12, textAlign: "center", cursor: "default" }}>
        No return leg for this fare
      </div>
    );
  }

  return (
    <div
      style={isSelected ? S.legCardSelected : S.legCard}
      className={`split-leg-card split-return-card${isSelected ? " selected" : ""}`}
      onClick={onSelect}
    >
      <div style={S.legRow}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AirlineLogo airlineCode={d.retAirlineCode} bgColor={lc} textColor={lc} initials={d.logo} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#0F1B33" }}>{d.airline}</div>
            <div style={{ fontSize: 11, color: "#6B7385" }}>{d.retCode}</div>
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={S.timeBig}>{d.retDep}</div>
          <div style={S.timeSub}>{d.retOrigin}</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#6B7385" }}>{formatDur(d.retDurMin)}</div>
          <span
            style={d.retStops === 0 ? S.badgeGreen : S.badgeAmber}
            title={
              d.retStops > 0 && d.retLayovers.length > 0
                ? d.retLayovers.map((l) => `${l.airportCode}${l.durationLabel ? ` · ${l.durationLabel} layover` : ""}`).join(", ")
                : undefined
            }
          >
            {d.retStops === 0
              ? "Non-stop"
              : d.retLayovers.length > 0
                ? `${d.retStops} stop${d.retStops > 1 ? "s" : ""} · ${d.retLayovers.map((l) => l.airportCode).join(", ")}`
                : `${d.retStops} stop${d.retStops > 1 ? "s" : ""}`}
          </span>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={S.timeBig}>{d.retArr}</div>
          <div style={S.timeSub}>{d.retDest}</div>
        </div>
      </div>

      <div style={S.legBookRow}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={d.refundable ? S.badgeGreen : S.badgeAmber}>
            {d.refundable ? "✓ Refundable" : "✕ Non-Refundable"}
          </span>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            {combined && <span style={{ fontSize: 10.5, color: "#6B7385" }}>Round trip total</span>}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={S.priceAmt}>₹{Math.round(d.price).toLocaleString("en-IN")}</span>
              {showTds && <TdsBadge amount={d.tdsAmount} />}
            </div>
          </div>
        </div>
        <button
          style={isSelected ? S.selectBtnActive : S.selectBtn}
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
        >
          {isSelected ? <><IconCheck /> Selected</> : "Select"}
        </button>
      </div>
    </div>
  );
}

// ====================== FIXED BOTTOM BOOKING BAR ======================
function BookingBar({ onwardFlight, returnFlight, sessionData, isDomestic, searchData, passengersArray, onClear, showTds, allContracts }) {
  const navigate = useNavigate();
  const [showFares, setShowFares] = useState(false);
  const dOn = getDisplayData(onwardFlight);
  const dRet = getDisplayData(returnFlight);

  // International combined: onward aur return dono same contract hote hain
  const sameContract = onwardFlight === returnFlight;
  const totalRaw = sameContract ? dOn.price : dOn.price + dRet.price;
  const totalTds = sameContract ? dOn.tdsAmount : dOn.tdsAmount + dRet.tdsAmount;
  const totalDisplay = `₹${Math.round(totalRaw).toLocaleString("en-IN")}`;
  const onwardPriceDisplay = `₹${Math.round(dOn.price).toLocaleString("en-IN")}`;
  const returnPriceDisplay = sameContract
    ? "Included"
    : `₹${Math.round(dRet.price).toLocaleString("en-IN")}`;

  const onwardRouteName = `${dOn.originCity || dOn.origin} (${dOn.origin}) → ${dOn.destCity || dOn.dest} (${dOn.dest})`;
  const returnRouteName = `${dRet.retOriginCity || dRet.retOrigin} (${dRet.retOrigin}) → ${dRet.retDestCity || dRet.retDest} (${dRet.retDest})`;

  return (
    <div style={S.bookingBarWrap} className="split-booking-bar">
      <div style={S.bookingBarInner}>
        <div style={S.bookingLegChip}>
          <span style={{ ...S.bookingLegTag, background: "#1a73e8" }}>Onward</span>
          <div style={{ minWidth: 0 }}>
            <div style={S.bookingLegRouteName}>{onwardRouteName}</div>
            <div style={S.bookingLegMeta}>
              {dOn.airline} {dOn.code} · {formatDateShort(dOn.depDate)}
            </div>
            <div style={S.bookingLegRoute}>
              <IconPlane style={{ transform: "rotate(45deg)" }} />
              {dOn.dep} → {dOn.arr}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
            <span style={{ ...S.bookingLegPrice, marginLeft: 0 }}>{onwardPriceDisplay}</span>
            {showTds && <TdsBadge amount={dOn.tdsAmount} light />}
          </div>
        </div>

        <div style={S.bookingDivider} />

        <div style={S.bookingLegChip}>
          <span style={{ ...S.bookingLegTag, background: "#e91e63" }}>Return</span>
          <div style={{ minWidth: 0 }}>
            <div style={S.bookingLegRouteName}>{returnRouteName}</div>
            <div style={S.bookingLegMeta}>
              {dRet.airline} {dRet.retCode} · {formatDateShort(dRet.retDepDate)}
            </div>
            <div style={S.bookingLegRoute}>
              <IconPlane style={{ transform: "rotate(225deg)" }} />
              {dRet.retDep} → {dRet.retArr}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
            <span style={{ ...S.bookingLegPrice, marginLeft: 0 }}>{returnPriceDisplay}</span>
            {showTds && !sameContract && <TdsBadge amount={dRet.tdsAmount} light />}
          </div>
        </div>

        <div style={S.bookingTotalBlock}>
          <div style={S.bookingTotalLabel}>Total</div>
          <div style={S.bookingTotalAmt}>{totalDisplay}</div>
          {showTds && (
            <div style={{ fontSize: 10.5, color: "#bfe0ff", marginTop: 2 }}>
              TDS ₹{Math.round(totalTds).toLocaleString("en-IN")}
            </div>
          )}
        </div>

        <button style={S.bookingClearBtn} onClick={onClear}>Clear</button>

        <button style={S.bookNowBtn} onClick={() => setShowFares(true)}>
          BOOK NOW
        </button>
      </div>

      {showFares && (
        <FareSelectModal
          legs={[
            { label: "Onward", flight: onwardFlight },
            ...(sameContract ? [] : [{ label: "Return", flight: returnFlight }]),
          ]}
          onClose={() => setShowFares(false)}
          onConfirm={(fares) => {
            setShowFares(false);
            const selOnward = applySelectedFare(onwardFlight, fares[0], allContracts);
            const selReturn = sameContract
              ? selOnward
              : applySelectedFare(returnFlight, fares[1], allContracts);

            navigate("/FareDetails", {
              state: {
                flight: selOnward,
                returnFlight: selReturn,
                selectedFare: fares[0],
                selectedReturnFare: fares[1] || null,
                allContracts,
                apiToken: selOnward?._apiToken ?? sessionData?.apiToken,
                bookingKey: selOnward?._bookingKey ?? sessionData?.bookingKey,
                returnApiToken: selReturn?._apiToken ?? sessionData?.apiToken,
                returnBookingKey: selReturn?._bookingKey ?? sessionData?.bookingKey,
                agencyKey: sessionData?.agencyKey,
                IsDomestic: isDomestic,
                passengers: passengersArray,
                AdultCount: searchData?.AdultCount || 1,
                ChildCount: searchData?.ChildCount || 0,
                InfantCount: searchData?.InfantCount || 0,
              },
            });
          }}
        />
      )}
    </div>
  );
}

function GlobalStyles() {
  return (
    <style>{`
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
  .td-root, .td-root button, .td-root input { font-family: 'Manrope', 'Segoe UI', system-ui, sans-serif; }
  .td-root { font-variant-numeric: tabular-nums; -webkit-font-smoothing: antialiased; }
  .td-root button:focus-visible,
  .td-root input:focus-visible,
  .td-root [tabindex]:focus-visible { outline: 2px solid #1a73e8; outline-offset: 2px; }
  .fc-card { transition: box-shadow .18s ease, border-color .18s ease; }
  .fc-card:hover { border-color: #C9D5EC; box-shadow: 0 8px 24px rgba(15,27,51,.09); }
  .btn-primary:hover { background: #1558b8 !important; }
  .btn-lock:hover { background: #FFF3DC !important; }
  .split-leg-card:not(.selected):hover { border-color: #9DBAEF; }
  .td-root ::-webkit-scrollbar { width: 8px; }
  .td-root ::-webkit-scrollbar-thumb { background: #CDD5E5; border-radius: 8px; }
  @media (max-width: 1024px) {
    .td-main { flex-direction: column; }
    .td-filter { width: 100% !important; box-sizing: border-box; }
  }
  @media (max-width: 860px) {
    .split-container { grid-template-columns: 1fr !important; }
  }
  @media (prefers-reduced-motion: reduce) {
    .td-root * { transition: none !important; }
  }
`}</style>
  );
}

// ====================== KUDO CHATBOT ======================
const KD_BOT_NAME = "Kudo";

// Chat shuru hote hi pehle yahi sawaal poochha jata hai
const KD_LANG_PROMPT =
  "Hi! I'm Kudo, your flight assistant.\n\nWould you like to chat in English or Hindi?\nType E for English.\nType H for Hindi.\n\n(Aap English me baat karenge ya Hindi me? English ke liye E likhein, Hindi ke liye H likhein.)";

const KD_SLOT_WORDS = [
  { slot: 1, label: "early morning (12-6 AM)", words: ["early morning", "raat", "midnight", "red eye", "redeye"] },
  { slot: 2, label: "morning (6 AM-12 PM)", words: ["morning", "subah"] },
  { slot: 3, label: "afternoon (12-6 PM)", words: ["afternoon", "dopahar", "noon"] },
  { slot: 4, label: "evening (6 PM-12 AM)", words: ["evening", "shaam", "night"] },
];

const kdHas = (t, words) => words.some((w) => t.includes(w));
const kdMoney = (v) => `₹${Math.round(v || 0).toLocaleString("en-IN")}`;
const kdFmtDur = (val) => {
  if (!val) return "N/A";
  if (typeof val === "string") return val;
  return `${Math.floor(val / 60)}h ${val % 60}m`;
};

// "under 5000", "5000 se kam", "5k tak" -> 5000
const kdParseBudget = (t) => {
  if (!kdHas(t, ["under", "below", "less than", "within", "budget", "se kam", "tak", "max", "upto", "up to"])) return null;
  const m = t.match(/(\d[\d,]*(?:\.\d+)?)\s*(k|hazar|hazaar|thousand)?/);
  if (!m) return null;
  let n = parseFloat(m[1].replace(/,/g, ""));
  if (m[2]) n *= 1000;
  return n > 0 ? n : null;
};

function KudoChat({
  flights = [],        // abhi list me jo flights dikh rahi hain
  allFlights = [],     // API se aayi saari flights (airline list ke liye)
  filters,
  setFilters,
  setSortTab,
  priceRange = { min: 0, max: 300000 },
  searchData,
  getDisplayData,
  raise = false,       // bottom booking bar dikh raha ho to button upar uthao
}) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [lang, setLang] = useState(null); // null = abhi poochha nahi, "E" = English, "H" = Hindi
  const [messages, setMessages] = useState([{ role: "bot", text: KD_LANG_PROMPT }]);
  const bodyRef = useRef(null);

  const route = searchData?.Routs?.[0];
  const routeText = route ? `${route.Source} → ${route.Destination}` : "this route";

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, typing, open]);

  // flights ko ek baar display data me badal lo
  const rows = useMemo(
    () => flights.map((f) => ({ f, d: getDisplayData(f) })),
    [flights, getDisplayData]
  );

  const airlines = useMemo(() => {
    const map = new Map();
    allFlights.forEach((f) => {
      const d = getDisplayData(f);
      if (d.airlineCode && !map.has(d.airlineCode)) map.set(d.airlineCode, d.airline);
    });
    return Array.from(map.entries()).map(([code, name]) => ({ code, name }));
  }, [allFlights, getDisplayData]);

  const describe = ({ d }) =>
    `${d.airline} ${d.code}\n${d.dep} → ${d.arr} (${kdFmtDur(d.durMin)}), ${
      d.stops === 0 ? "Non-stop" : `${d.stops} stop${d.stops > 1 ? "s" : ""}`
    }\n${kdMoney(d.price)} per person · ${d.refundable ? "Refundable" : "Non-refundable"}`;

  const pickCheapest = () =>
    rows.length ? [...rows].sort((a, b) => a.d.price - b.d.price)[0] : null;
  const pickFastest = () =>
    rows.length
      ? [...rows].sort((a, b) => (a.d.totalDurMin === b.d.totalDurMin ? a.d.price - b.d.price : a.d.totalDurMin - b.d.totalDurMin))[0]
      : null;

  const baseFilters = { maxPrice: priceRange.max, stops: [], depSlots: [], airlines: [], refundable: [] };

  const welcomeText = (L) =>
    L === "E"
      ? `Great, we'll chat in English! I'm Kudo. For ${routeText} I can find the cheapest or fastest flight, apply filters, and answer questions about baggage, refunds and more.\n\nType "help" anytime to see what I can do. (To switch language, type E or H.)`
      : `Badiya, hum Hindi me baat karenge! Main Kudo hoon. ${routeText} ki flights me main sasti ya tez flight dhoondh sakta hoon, filters laga sakta hoon, aur baggage, refund jaise sawaalon ke jawab de sakta hoon.\n\nKabhi bhi "help" likho to dekho main kya kar sakta hoon. (Language badalne ke liye E ya H likho.)`;

  // ===== main brain: text -> reply (L = "E" ya "H") =====
  const getReply = (raw, L) => {
    const t = raw.toLowerCase().trim();
    const tr = (en, hi) => (L === "E" ? en : hi);

    // 0) Language switch beech me bhi
    if (["e", "english", "eng"].includes(t)) {
      setLang("E");
      return "Okay, switching to English. How can I help you?";
    }
    if (["h", "hindi", "hin"].includes(t)) {
      setLang("H");
      return "Theek hai, ab Hindi me baat karte hain. Bataiye, kya madad karoon?";
    }

    // 1) Filters / sort commands (ek saath kai ho sakte hain)
    const patch = {};
    const notes = [];

    if (kdHas(t, ["non stop", "non-stop", "nonstop", "direct", "bina stop", "without stop"])) {
      patch.stops = [0];
      notes.push(tr("non-stop only", "sirf non-stop"));
    } else if (kdHas(t, ["1 stop", "one stop", "with stop", "connecting"])) {
      patch.stops = [1, 2];
      notes.push(tr("connecting flights", "connecting flights"));
    }

    const slotHit = KD_SLOT_WORDS.find((s) => kdHas(t, s.words));
    if (slotHit) {
      patch.depSlots = [slotHit.slot];
      notes.push(slotHit.label);
    }

    if (kdHas(t, ["non refundable", "non-refundable"])) {
      patch.refundable = [false];
      notes.push("non-refundable");
    } else if (kdHas(t, ["refundable", "refund wali", "refund wala"])) {
      patch.refundable = [true];
      notes.push("refundable");
    }

    const airlineHit = airlines.find(
      (a) => t.includes(a.name.toLowerCase()) || new RegExp(`\\b${a.code.toLowerCase()}\\b`).test(t)
    );
    if (airlineHit) {
      patch.airlines = [airlineHit.code];
      notes.push(airlineHit.name);
    }

    const budget = kdParseBudget(t);
    if (budget) {
      const cap = Math.min(Math.max(budget, priceRange.min), priceRange.max);
      patch.maxPrice = cap;
      notes.push(tr(`under ${kdMoney(budget)}`, `${kdMoney(budget)} tak`));
    }

    if (Object.keys(patch).length > 0) {
      setFilters((f) => ({ ...f, ...patch }));
      return tr(
        `Done! I've applied these filters: ${notes.join(", ")}. The list is updated.\nIf no flights show up, type "reset filters".`,
        `Done! Maine filter laga diye: ${notes.join(", ")}. List update ho gayi hai.\nAgar koi flight na dikhe to "reset filters" likho.`
      );
    }

    if (kdHas(t, ["reset", "clear filter", "filters hata", "filter hata", "remove filter"])) {
      setFilters(baseFilters);
      return tr(
        "All filters cleared. You can now see all flights.",
        "Saare filters hata diye. Ab saari flights dikh rahi hain."
      );
    }

    // 2) Greetings
    if (/^(hi|hii|hello|hey|namaste|hlo|yo)\b/.test(t)) {
      return tr(
        `Hi! I'm ${KD_BOT_NAME}, your flight assistant. For ${routeText} I can find the cheapest/fastest flight, apply filters, and answer questions on baggage, refunds and more.`,
        `Hi! Main ${KD_BOT_NAME} hoon, aapka flight assistant. ${routeText} ki flights me main sasti/tez flight dhoondh sakta hoon, filters laga sakta hoon, aur baggage, refund jaise sawaalon ke jawab de sakta hoon.`
      );
    }

    const emptyMsg = tr(
      "There are no flights in the list right now. Try resetting the filters.",
      "Abhi list me koi flight nahi hai. Filters reset karke dekho."
    );

    // 3) Results ke baare me
    if (kdHas(t, ["cheapest", "sasta", "sasti", "lowest", "kam price", "minimum price", "cheap"])) {
      setSortTab("Cheapest");
      const c = pickCheapest();
      return c
        ? tr(
            `Cheapest flight:\n${describe(c)}\n\nI've also sorted the list by "Cheapest".`,
            `Sabse sasti flight:\n${describe(c)}\n\nList ko "Cheapest" se sort bhi kar diya hai.`
          )
        : emptyMsg;
    }

    if (kdHas(t, ["fastest", "jaldi", "quickest", "shortest", "kam time", "sabse tez", "fast"])) {
      setSortTab("Fastest");
      const f = pickFastest();
      return f
        ? tr(
            `Fastest flight:\n${describe(f)}\n\nI've also sorted the list by "Fastest".`,
            `Sabse tez flight:\n${describe(f)}\n\nList ko "Fastest" se sort kar diya hai.`
          )
        : emptyMsg;
    }

    if (kdHas(t, ["best", "recommend", "suggest", "konsi", "kaunsi", "which flight", "behtar"])) {
      setSortTab("Best");
      const c = pickCheapest();
      const f = pickFastest();
      if (!c) return emptyMsg;
      if (c.f === f.f) {
        return tr(
          `This flight is both the cheapest and the fastest:\n${describe(c)}`,
          `Ye flight sasti bhi hai aur sabse tez bhi:\n${describe(c)}`
        );
      }
      const diff = Math.round(f.d.price - c.d.price);
      const more = diff >= 0;
      return tr(
        `Two good options:\n\nCheapest: ${describe(c)}\n\nFastest: ${describe(f)}\n\nThe fastest one costs ${kdMoney(Math.abs(diff))} ${more ? "more" : "less"}. Pick the cheapest to save money, or the fastest to save time.`,
        `Do achhe options:\n\nSasti: ${describe(c)}\n\nTez: ${describe(f)}\n\nTez wali ${kdMoney(Math.abs(diff))} ${more ? "zyada" : "kam"} hai. Budget ke liye sasti, time bachane ke liye tez wali lo.`
      );
    }

    if (kdHas(t, ["compare", "difference", "farak"])) {
      const c = pickCheapest();
      const f = pickFastest();
      if (!c) return tr("There are no flights to compare right now.", "Compare karne ke liye abhi list me flights nahi hain.");
      return tr(
        `Cheapest vs Fastest:\n\nCheapest → ${c.d.airline} ${c.d.code}, ${kdMoney(c.d.price)}, ${kdFmtDur(c.d.durMin)}\nFastest → ${f.d.airline} ${f.d.code}, ${kdMoney(f.d.price)}, ${kdFmtDur(f.d.durMin)}`,
        `Sasti vs Tez:\n\nSasti → ${c.d.airline} ${c.d.code}, ${kdMoney(c.d.price)}, ${kdFmtDur(c.d.durMin)}\nTez → ${f.d.airline} ${f.d.code}, ${kdMoney(f.d.price)}, ${kdFmtDur(f.d.durMin)}`
      );
    }

    if (kdHas(t, ["how many", "kitni", "kitne", "count", "total flights", "kul"])) {
      return tr(
        `${rows.length} flights are showing right now (${routeText}). This number changes when you change filters.`,
        `Abhi ${rows.length} flights dikh rahi hain (${routeText}). Filters badalne par ye number badlega.`
      );
    }

    if (kdHas(t, ["price range", "kitne ka", "kitna price", "price kya", "rate", "kimat", "kitna lagega", "fare"])) {
      if (!rows.length) return tr("There are no flights in the list right now.", "Abhi list me koi flight nahi hai.");
      const prices = rows.map((r) => r.d.price);
      return tr(
        `Fares for ${routeText} range from ${kdMoney(Math.min(...prices))} to ${kdMoney(Math.max(...prices))} (per person).`,
        `${routeText} ka fare ${kdMoney(Math.min(...prices))} se ${kdMoney(Math.max(...prices))} tak hai (per person).`
      );
    }

    if (kdHas(t, ["airline", "airlines", "konsi airline", "kaun si airline", "operating"])) {
      if (!airlines.length) return tr("The airline list hasn't loaded yet.", "Airline list abhi load nahi hui.");
      const list = airlines.map((a) => `${a.name} (${a.code})`).join(", ");
      return tr(
        `These airlines fly this route: ${list}.\nType an airline name (like "IndiGo flights") and I'll filter the list.`,
        `Is route par ye airlines hain: ${list}.\nKisi ka naam likho (jaise "IndiGo flights") to main filter laga dunga.`
      );
    }

    // 4) FAQs
    if (kdHas(t, ["baggage", "luggage", "bag", "saman", "kg", "cabin"])) {
      const c = pickCheapest();
      const extra = c
        ? tr(
            `\n\nOn the cheapest flight (${c.d.airline}): Cabin ${c.d.handBag}, Check-in ${c.d.baggage}.`,
            `\n\nSabse sasti flight (${c.d.airline}) me: Cabin ${c.d.handBag}, Check-in ${c.d.baggage}.`
          )
        : "";
      return tr(
        `Baggage depends on the airline and fare type. In Economy you usually get 15 kg check-in and 7 kg cabin. When you click "Book now", the fare table shows the baggage for each fare.${extra}`,
        `Baggage airline aur fare type par depend karta hai. Economy me aam taur par 15 kg check-in aur 7 kg cabin milta hai. "Book now" par fare select karte time har fare ka baggage table dikhta hai.${extra}`
      );
    }

    if (kdHas(t, ["cancel", "refund", "paise wapas", "money back", "date change", "reschedule"])) {
      const ref = rows.filter((r) => r.d.refundable).length;
      return tr(
        `Cancellation and date-change charges depend on the fare type. Click "Book now" to see the Cancellation fee and Date change fee in the fare table, or open "Flight details" and check the "Cancellation & Change Rule" tab.\n\nThere are ${ref} refundable flights in the list right now. Type "refundable" to see only those.`,
        `Cancellation/date change ke charges fare type par depend karte hain. "Book now" par fare table me Cancellation fee aur Date change fee dikhti hai, aur "Flight details" me "Cancellation & Change Rule" tab hai.\n\nAbhi list me ${ref} refundable flights hain. Sirf wahi dekhni ho to likho "refundable".`
      );
    }

    if (kdHas(t, ["document", "id proof", "passport", "aadhaar", "aadhar", "visa", "pan card", "license"])) {
      return tr(
        "For domestic flights, one valid government photo ID is enough (Aadhaar, PAN, Driving License or Passport). For international trips you need a passport with at least 6 months of validity, and a visa if required. Keep an ID or birth proof for children too.",
        "Domestic flight ke liye ek valid government photo ID chahiye (Aadhaar, PAN, Driving License ya Passport). International trip ke liye passport (6 mahine ki validity) aur zaroorat ho to visa. 18 saal se chhote bacchon ke liye bhi ID/birth proof saath rakho."
      );
    }

    if (kdHas(t, ["web check", "check-in", "check in", "checkin", "airport kab", "kab pahunch", "reporting"])) {
      return tr(
        "For domestic flights, reach the airport at least 2 hours before departure. Web check-in usually opens 48 hours to 2 hours before departure (varies by airline) and saves you the airport queue.",
        "Domestic flight ke liye departure se kam se kam 2 ghante pehle airport pahunchna theek rehta hai. Web check-in aam taur par 48 se 2 ghante pehle khulta hai (airline ke hisaab se), isse airport par line bachti hai."
      );
    }

    if (kdHas(t, ["child", "infant", "baby", "bachcha", "bachche", "bacche", "kid"])) {
      return tr(
        "Adult: 12+ years, Child: 2-12 years, Infant: under 2 years. An infant doesn't get a separate seat (sits on a lap) and has a separate fare. Each adult can carry 1 infant at most. Use the Modify button at the top to change travellers.",
        "Adult: 12+ saal, Child: 2-12 saal, Infant: 2 saal se chhote. Infant ki alag seat nahi hoti (lap me baithta hai), isliye uska fare alag hota hai. Ek adult ke saath max 1 infant allowed hai. Traveller badalne ke liye upar Modify button use karo."
      );
    }

    if (kdHas(t, ["lock", "hold", "price lock"])) {
      return tr(
        'The "Lock Price" button on a flight card lets you hold the fare for a small fee, so the price won\'t change. The fee is about 5% of the fare (minimum ₹199, maximum ₹999).',
        'Flight card par "Lock Price" button se tum thodi si fee dekar fare lock kar sakte ho, taaki price badle nahi. Fee fare ka lagbhag 5% hoti hai (minimum ₹199, maximum ₹999).'
      );
    }

    if (kdHas(t, ["layover", "stop kya", "connecting kya", "transit"])) {
      return tr(
        'A stop means the flight halts at another airport on the way. A card showing "1 stop · BOM (2h 10m)" means a 2h 10m layover in Mumbai. If you want a direct flight, type "non-stop".',
        'Stop matlab flight beech me kisi airport par rukti hai. Card par "1 stop · BOM (2h 10m)" jaisa likha hota hai, yaani Mumbai me 2h 10m ka layover. Seedhi flight chahiye to likho "non-stop".'
      );
    }

    if (kdHas(t, ["seat", "meal", "khana", "food"])) {
      return tr(
        'Click "Book now" and the fare table shows the charges for Seat selection and Meal selection. Some fares include them free, others charge extra.',
        '"Book now" par fare table me Seat selection aur Meal selection ke charges dikhte hain. Kuch fares me free hote hain, kuch me paid.'
      );
    }

    if (kdHas(t, ["payment", "pay", "upi", "card", "wallet", "gst"])) {
      return tr(
        "After you select a fare, you'll go to the passenger details and payment page, where the available payment options are shown.",
        "Fare select karke aap passenger details aur payment page par jaoge. Wahan available payment options dikhenge."
      );
    }

    if (kdHas(t, ["modify", "change route", "date badal", "route badal", "dusri date", "another date", "different city"])) {
      return tr(
        'To change the route, date or travellers, click the "Modify" button at the top of the page and run a new search.',
        'Route, date ya travellers badalne ke liye page ke upar "Modify" button dabao, wahan se nayi search kar sakte ho.'
      );
    }

    if (kdHas(t, ["book", "kaise book", "how to book"])) {
      return tr(
        'Pick a flight, click "Book now" on its card, choose a fare and press Continue. For round trips, first select the onward and return flights, then use the BOOK NOW bar at the bottom.',
        'Flight chuno, uske card par "Book now" dabao, fare select karo, aur Continue karo. Round trip me pehle onward aur return flight select karo, phir neeche BOOK NOW bar aayega.'
      );
    }

    if (kdHas(t, ["thanks", "thank you", "shukriya", "dhanyawad", "thx"])) {
      return tr("Happy to help! Ask me anything else anytime.", "Khushi hui madad karke! Aur kuch chahiye to pooch lena.");
    }

    if (kdHas(t, ["help", "kya kar sakte", "what can you", "madad"])) {
      return tr(
        `Here's what I can do:\n• Find the cheapest / fastest flight\n• Apply filters ("morning non-stop", "IndiGo", "under 5000", "refundable")\n• Answer questions on baggage, refunds, documents, check-in\n• Type "reset filters" to clear everything`,
        `Main ye kar sakta hoon:\n• Sasti / tez flight batana\n• Filters lagana ("morning non-stop", "IndiGo", "5000 se kam", "refundable")\n• Baggage, refund, documents, check-in jaise sawaalon ke jawab\n• "reset filters" se sab hatana`
      );
    }

    return tr(
      `I didn't understand that. You can try:\n• "Cheapest flight"\n• "Morning non-stop"\n• "Under 5000"\n• "How much baggage do I get?"`,
      `Ye mujhe samajh nahi aaya. Aap aisa try kar sakte ho:\n• "Cheapest flight batao"\n• "Morning non-stop dikhao"\n• "5000 se kam"\n• "Baggage kitna milega?"`
    );
  };

  const send = (text) => {
    const q = (text ?? input).trim();
    if (!q) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setTyping(true);

    setTimeout(() => {
      let answer;

      // Pehle language poochhi jaati hai
      if (!lang) {
        const c = q.toLowerCase();
        let chosen = null;
        if (["e", "english", "eng"].includes(c)) chosen = "E";
        else if (["h", "hindi", "hin"].includes(c)) chosen = "H";

        if (chosen) {
          setLang(chosen);
          answer = welcomeText(chosen);
        } else {
          answer =
            "Please type E for English or H for Hindi.\n(Kripya English ke liye E ya Hindi ke liye H likhein.)";
        }
      } else {
        try {
          answer = getReply(q, lang);
        } catch (e) {
          console.error("Chatbot error:", e);
          answer =
            lang === "E" ? "Something went wrong, please try again." : "Kuch gadbad ho gayi, dobara try karo.";
        }
      }

      setMessages((m) => [...m, { role: "bot", text: answer }]);
      setTyping(false);
    }, 450);
  };

  const quick =
    lang === "E"
      ? ["Cheapest flight", "Which is the fastest flight?", "Morning non-stop", "Only refundable", "How much baggage?"]
      : ["Cheapest flight batao", "Fastest flight kaunsi hai?", "Morning non-stop dikhao", "Sirf refundable", "Baggage kitna milega?"];

  // Language choose hone se pehle E / H buttons, uske baad quick questions (sirf shuru me)
  const showLangChips = !lang;
  const showQuickChips = !!lang && messages.length <= 3;

  const placeholder = !lang ? "Type E or H..." : lang === "E" ? "Ask me anything..." : "Kuch bhi poocho...";

  return (
    <>
      <style>{`
        .kd-btn { position: fixed; right: 20px; z-index: 600; display: flex; align-items: center; gap: 10px;
          background: #fff; border: none; border-radius: 999px; padding: 8px 20px 8px 8px; cursor: pointer;
          box-shadow: 0 8px 24px rgba(6,26,51,.22); transition: transform .2s ease, bottom .2s ease; font-family: inherit; }
        .kd-btn:hover { transform: translateY(-2px); }
        .kd-ico { width: 40px; height: 40px; border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #1a73e8, #0B2A5E); }
        .kd-btn-text { font-size: 14px; font-weight: 700; color: #0B2A5E; }
        .kd-box { position: fixed; right: 20px; z-index: 700; width: 370px; max-width: calc(100vw - 24px);
          height: 540px; max-height: calc(100vh - 100px); background: #fff; border-radius: 18px; display: flex; flex-direction: column;
          box-shadow: 0 20px 50px rgba(6,26,51,.32); overflow: hidden; font-family: 'Manrope','Segoe UI',system-ui,sans-serif; color: #0F1B33; }
        .kd-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px;
          background: linear-gradient(120deg, #0B2A5E, #1a73e8); color: #fff; }
        .kd-title { font-size: 17px; font-weight: 800; display: flex; align-items: center; gap: 8px; }
        .kd-sub { font-size: 11.5px; opacity: .8; font-weight: 500; }
        .kd-x { width: 32px; height: 32px; border-radius: 50%; border: none; background: rgba(255,255,255,.18); color: #fff; cursor: pointer; font-size: 14px; }
        .kd-body { flex: 1; overflow-y: auto; padding: 14px; background: #F6F8FC; display: flex; flex-direction: column; gap: 10px; }
        .kd-msg { max-width: 86%; padding: 10px 13px; border-radius: 14px; font-size: 13.5px; line-height: 1.45; white-space: pre-line; word-break: break-word; }
        .kd-user { align-self: flex-end; background: #0B2A5E; color: #fff; border-bottom-right-radius: 4px; }
        .kd-bot { align-self: flex-start; background: #fff; border: 1px solid #E8EAF0; border-bottom-left-radius: 4px; }
        .kd-typing { color: #6B7385; font-style: italic; }
        .kd-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .kd-chip { border: 1px solid #C9D5EC; background: #fff; color: #1a73e8; border-radius: 999px; padding: 7px 12px;
          font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; }
        .kd-chip:hover { background: #EEF5FF; }
        .kd-chip-lang { padding: 9px 18px; font-size: 13.5px; font-weight: 700; }
        .kd-bar { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-top: 1px solid #E8EAF0; background: #fff; }
        .kd-input { flex: 1; min-width: 0; border: none; outline: none; font-size: 16px; font-family: inherit; color: #0F1B33; background: transparent; }
        .kd-send { width: 38px; height: 38px; border-radius: 50%; border: none; background: #1a73e8; cursor: pointer;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .kd-send:disabled { opacity: .5; cursor: not-allowed; }
        @media (max-width: 560px) {
          .kd-btn-text { display: none; }
          .kd-btn { padding: 8px; right: 14px; }
          .kd-box { right: 0; left: 0; bottom: 0 !important; width: 100%; max-width: 100%; max-height: 92vh; height: 92vh; border-radius: 18px 18px 0 0; }
        }
      `}</style>

      {!open && (
        <button className="kd-btn" style={{ bottom: raise ? 110 : 20 }} onClick={() => setOpen(true)} aria-label={`Open ${KD_BOT_NAME} chat`}>
          <span className="kd-ico">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="5" width="18" height="14" rx="4" />
              <circle cx="9" cy="12" r="1" />
              <circle cx="15" cy="12" r="1" />
            </svg>
          </span>
          <span className="kd-btn-text">Ask {KD_BOT_NAME}</span>
        </button>
      )}

      {open && (
        <div className="kd-box" style={{ bottom: raise ? 110 : 20 }} role="dialog" aria-label={`${KD_BOT_NAME} chat`}>
          <div className="kd-head">
            <div>
              <div className="kd-title">{KD_BOT_NAME}</div>
              <div className="kd-sub">Flight assistant · {routeText}</div>
            </div>
            <button className="kd-x" onClick={() => setOpen(false)} aria-label="Close">✕</button>
          </div>

          <div className="kd-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <div key={i} className={`kd-msg ${m.role === "user" ? "kd-user" : "kd-bot"}`}>{m.text}</div>
            ))}

            {showLangChips && (
              <div className="kd-chips">
                <button className="kd-chip kd-chip-lang" onClick={() => send("E")}>E · English</button>
                <button className="kd-chip kd-chip-lang" onClick={() => send("H")}>H · Hindi</button>
              </div>
            )}

            {showQuickChips && (
              <div className="kd-chips">
                {quick.map((q) => (
                  <button key={q} className="kd-chip" onClick={() => send(q)}>{q}</button>
                ))}
              </div>
            )}

            {typing && <div className="kd-msg kd-bot kd-typing">Typing...</div>}
          </div>

          <div className="kd-bar">
            <input
              className="kd-input"
              placeholder={placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
            />
            <button className="kd-send" onClick={() => send()} disabled={typing || !input.trim()} aria-label="Send">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <path d="M12 19V5M12 5l-6 6M12 5l6 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}


export default function Index() {
  const location = useLocation();
  const navigate = useNavigate();

  const [flights, setFlights]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [showModify, setShowModify]   = useState(false);
  // NEW: user ne button se view badla hai ya nahi. null = default (domestic -> split, international -> combined)
  const [userView, setUserView]       = useState(null); // null | "split" | "combined"
  // NEW: International me TDS button se on/off hota hai
  const [showTds, setShowTds]         = useState(false);
  const [selectedOnward, setSelectedOnward] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [tripType, setTripType]       = useState("One way");
  const [sortTab, setSortTab]         = useState("Best");
  const [filters, setFilters]         = useState({ maxPrice: 300000, stops: [], depSlots: [], airlines: [], refundable: [] });
  const [sessionData, setSessionData] = useState({ apiToken: "", agencyKey: "", bookingKey: "" });
  const ITEMS_PER_PAGE = 10;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  // NEW: International detect karne ke liye airports ka country use hota hai
  // (URL se aayi search me IsDomestic set nahi hota, isliye sirf flag par depend nahi karte)
  const [airports, setAirports] = useState([]);
  useEffect(() => { loadAirportsShared(setAirports); }, []);

  const detectedDomestic = useMemo(() => {
    const route = location.state?.searchData?.Routs?.[0];
    const src = route?.Source, dst = route?.Destination;
    if (!src || !dst) return null;
    const isIn = (code) => isIndianAirportCode(code, airports);
    const f = isIn(src), t = isIn(dst);
    if (f === null || t === null) return null;
    return f && t;
  }, [airports, location.state]);

  const isDomestic = detectedDomestic ?? (location.state?.searchData?.IsDomestic ?? true);
  const isRoundTripSearch = (location.state?.searchData?.TripType === 1 || location.state?.searchData?.Routs?.length > 1);

  // Response me alag Onward + Return contracts hain? (domestic round trip = true,
  // international combined fares = false). IsDomestic flag par depend nahi karte.
  const isSplitData = useMemo(() => hasSplitContracts(flights), [flights]);
  // Pair-based flow (onward/return alag contracts) tabhi use hoga jab data waisa ho
  const showPairs = isRoundTripSearch && isSplitData;

  // NEW: International combined fares (ek contract me onward + return segments)
  const hasCombinedRT = useMemo(
    () => isRoundTripSearch && !isSplitData && flights.some((f) => getDisplayData(f).isRoundTrip),
    [isRoundTripSearch, isSplitData, flights]
  );

  // NEW: Toggle button tabhi dikhega jab dono me se koi ek round trip data ho
  const canToggleView = showPairs || hasCombinedRT;

  // NEW: View decide karna
  //  - Domestic (showPairs): default SPLIT, button se COMBINED
  //  - International (hasCombinedRT): default COMBINED, button se SPLIT
  const splitView = canToggleView
    ? (userView ? userView === "split" : showPairs)
    : false;

  const availableAirlines = useMemo(() => {
    const map = new Map();
    flights.forEach((f) => {
      const d = getDisplayData(f);
      if (d.airlineCode && !map.has(d.airlineCode)) {
        map.set(d.airlineCode, d.airline);
      }
    });
    return Array.from(map.entries())
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [flights]);

  const priceRange = useMemo(() => {
    const rawPrices = flights.map((f) => getDisplayData(f).price).filter((p) => p > 0);
    if (!rawPrices.length) return { min: 0, max: 300000 };

    const min = Math.min(...rawPrices);
    const max = Math.max(...rawPrices);

    if (min === max) {
      return { min: Math.max(0, min - 1), max: max + 1 };
    }
    return { min, max };
  }, [flights]);

  useEffect(() => {
    const sd = location.state?.searchData;
    if (sd) {
      setTripType(sd.TripType === 1 || (sd.Routs?.length || 0) > 1 ? "Round trip" : "One way");
    }
  }, [location.state]);

  useEffect(() => {
    const payload = location.state?.searchData;
    if (!payload) { navigate("/"); return; }
    let cancelled = false;
    const fetchFlights = async () => {
      try {
        setLoading(true);
        setSessionData({ apiToken: "", agencyKey: "", bookingKey: "" });
        setFlights([]);
        setError("");
        // NEW: Naya search -> view default par wapas (domestic split / international combined)
        setUserView(null);

        const baseURL = process.env.REACT_APP_FLIGHTS_API_URL;
        if (!baseURL) { setError("API configuration missing"); return; }

        const response = await fetch(`${baseURL}/Trip/AirGroupResults`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (cancelled) return;
        console.log("API Response:", data);
        console.log(
          "%cFresh Search -> BookingKey:",
          "color:#059669;font-weight:700",
          data?.BookingKey,
          "| APIToken:",
          data?.APIToken,
          "| Contracts count:",
          data?.Contracts?.length
        );

        // FIX: Error object na aaye (international response me) to ise error mat samjho
        const errCode = data?.Error?.ErrorCode;
        const hasError = errCode !== undefined && errCode !== null && Number(errCode) !== 0;

        if (hasError) {
          setError(data?.Error?.ErrorDesc || "API Error");
        } else {
          setSessionData({
            apiToken:   data?.APIToken   || "",
            agencyKey:  data?.AgencyKey  || "",
            bookingKey: data?.BookingKey || "",
          });
          // FIX: Contracts key ke alag-alag naam handle karo
          const rawContracts = data?.Contracts || data?.contracts || [];
          const stamped = rawContracts.map((c) => ({
            ...c,
            _apiToken: data?.APIToken,
            _bookingKey: data?.BookingKey,
          }));
          setFlights(stamped);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Fetch Error:", err);
        setError("Server Error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFlights();
    return () => { cancelled = true; };
  }, [location.state, navigate]);

  useEffect(() => {
    if (flights.length > 0) {
      setFilters((f) => ({ ...f, maxPrice: priceRange.max }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flights]);

  // View badalne (split <-> combined) par purani selection hata do
  useEffect(() => {
    setSelectedOnward(null);
    setSelectedReturn(null);
  }, [splitView]);

  // Filter + sort (sortFlights: Best / Cheapest / Fastest)
  const filteredFlights = useMemo(() => {
    const list = flights.filter((f) => {
      const d = getDisplayData(f);
      if (d.price > filters.maxPrice) return false;
      if (filters.stops.length && !filters.stops.includes(d.stops > 1 ? 2 : d.stops)) return false;
      if (filters.depSlots.length && !filters.depSlots.includes(timeSlot(d.dep))) return false;
      if (filters.airlines.length && !filters.airlines.includes(d.airlineCode)) return false;
      if (filters.refundable.length && !filters.refundable.includes(d.refundable)) return false;
      return true;
    });
    return sortFlights(list, sortTab);
  }, [flights, filters, sortTab]);

  const onwardFlights = useMemo(
    () => filteredFlights.filter((f) => isOnwardT(f.ContractType)),
    [filteredFlights]
  );
  const returnFlights = useMemo(
    () => filteredFlights.filter((f) => isReturnT(f.ContractType)),
    [filteredFlights]
  );

  // Onward jiska kam se kam ek valid return ho (same airline + time ke baad)
  const pairableOnward = useMemo(
    () => onwardFlights.filter((o) => returnFlights.some((r) => isValidPair(o, r))),
    [onwardFlights, returnFlights]
  );

  // Return jiska kam se kam ek valid onward ho
  const pairableReturn = useMemo(
    () => returnFlights.filter((r) => onwardFlights.some((o) => isValidPair(o, r))),
    [onwardFlights, returnFlights]
  );

  // Combined view: har onward ke liye uska sabse sasta VALID return
  const cheapestReturnMap = useMemo(() => {
    const map = new Map();
    pairableOnward.forEach((o) => {
      const valid = returnFlights.filter((r) => isValidPair(o, r));
      if (valid.length) map.set(o.ContractId, sortFlights(valid, "Cheapest")[0]);
    });
    return map;
  }, [pairableOnward, returnFlights]);

  // Split view (domestic): onward select hai to sirf uske valid returns, warna sab pairable returns
  const splitReturnList = useMemo(() => {
    if (selectedOnward?.flight) {
      return returnFlights.filter((r) => isValidPair(selectedOnward.flight, r));
    }
    return pairableReturn;
  }, [selectedOnward, returnFlights, pairableReturn]);

  // NEW: Split view (international combined fares): har contract ka onward leg left me,
  // return leg right me. Dono columns me same list, contract select karne par dono highlight hote hain.
  const combinedSplitList = useMemo(
    () => (hasCombinedRT ? filteredFlights.filter((f) => getDisplayData(f).isRoundTrip) : []),
    [hasCombinedRT, filteredFlights]
  );

  // Pair-based count sirf tab jab data split ho. International combined / one way me
  // filteredFlights ka count.
  const listCount = showPairs
    ? (splitView
        ? Math.max(pairableOnward.length, splitReturnList.length)
        : pairableOnward.length)
    : (splitView ? combinedSplitList.length : filteredFlights.length);

  // Onward badalne par agar selected return ab valid nahi raha to hata do
  // (International combined me onward/return same contract hote hain, isliye skip)
  useEffect(() => {
    if (
      selectedOnward?.flight &&
      selectedReturn?.flight &&
      selectedOnward.flight !== selectedReturn.flight &&
      !isValidPair(selectedOnward.flight, selectedReturn.flight)
    ) {
      setSelectedReturn(null);
    }
  }, [selectedOnward, selectedReturn]);

  useEffect(() => { setVisibleCount(ITEMS_PER_PAGE); }, [filters, sortTab, splitView]);

  useEffect(() => {
    if (splitView) return; // window-scroll pagination sirf combined view me
    const handleScroll = () => {
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300;
      if (scrolledToBottom) {
        setVisibleCount((prev) => (prev >= listCount ? prev : prev + ITEMS_PER_PAGE));
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [listCount, splitView]);

  const combinedList = useMemo(() => {
    // International / combined fare ke liye filteredFlights use hota hai
    const base = showPairs ? pairableOnward : filteredFlights;
    return base.slice(0, visibleCount);
  }, [showPairs, pairableOnward, filteredFlights, visibleCount]);

  const totalPages = Math.ceil(listCount / ITEMS_PER_PAGE) || 1;
  const currentPageDisplay = Math.min(Math.ceil(visibleCount / ITEMS_PER_PAGE), totalPages);
  const hasMore = visibleCount < listCount;

  // Har tab me uski apni top flight ka price + duration
  const tabMeta = useMemo(() => {
    const empty = { price: "-", dur: "-" };
    if (!filteredFlights.length) return { Best: empty, Cheapest: empty, Fastest: empty };

    const info = (f) => {
      const d = getDisplayData(f);
      return {
        price: d.price,
        dur: d.isRoundTrip ? formatMins(d.totalDurMin) : formatDur(d.durMin),
      };
    };

    return {
      Best:     info(sortFlights(filteredFlights, "Best")[0]),
      Cheapest: info(sortFlights(filteredFlights, "Cheapest")[0]),
      Fastest:  info(sortFlights(filteredFlights, "Fastest")[0]),
    };
  }, [filteredFlights]);

  const searchData = location.state?.searchData;
  const passengersArray = [];

  const handleSelectOnward = (flight) =>
    setSelectedOnward((prev) =>
      prev?.flight?.ContractId === flight.ContractId ? null : { flight }
    );
  const handleSelectReturn = (flight) =>
    setSelectedReturn((prev) =>
      prev?.flight?.ContractId === flight.ContractId ? null : { flight }
    );
  // NEW: International combined split me kisi bhi side se contract select karo -> onward + return dono set
  const handleSelectCombined = (flight) => {
    const already = selectedOnward?.flight?.ContractId === flight.ContractId;
    if (already) {
      setSelectedOnward(null);
      setSelectedReturn(null);
    } else {
      setSelectedOnward({ flight });
      setSelectedReturn({ flight });
    }
  };
  const clearSelection = () => {
    setSelectedOnward(null);
    setSelectedReturn(null);
  };

  const bookingBarActive = splitView && selectedOnward && selectedReturn;

  // Split view me kaunsi list kis column me jayegi
  const onwardColumnList = showPairs ? pairableOnward : combinedSplitList;
  const returnColumnList = showPairs ? splitReturnList : combinedSplitList;

  return (
    <div style={{ ...S.page, paddingBottom: bookingBarActive ? 110 : 0 }} className="td-root">
      <GlobalStyles />
      {loading && (
        <AirplaneLoader
          message={`Loading flights from ${searchData?.Routs?.[0]?.Source || "DEL"} to ${searchData?.Routs?.[0]?.Destination || "BOM"}...`}
        />
      )}
      <div style={S.topBar}>
        <div style={S.metaRow}>
          {[
            [isRoundTripSearch ? "Round Trip" : "One Way", `${searchData?.Routs?.[0]?.Source || "DEL"} → ${searchData?.Routs?.[0]?.Destination || "BOM"}`],
            ["📅 Departure", searchData?.Routs?.[0]?.TravelDate || ""],
            isRoundTripSearch ? ["📅 Return", searchData?.Routs?.[1]?.TravelDate || ""] : null,
            ["Traveller", `Adult: ${searchData?.AdultCount || 1}, Child: ${searchData?.ChildCount || 0}, Infant: ${searchData?.InfantCount || 0}`],
            ["Class", CLASS_NAMES[searchData?.TypeOfClass] ?? "Economy"],
          ].filter(Boolean).map(([lbl, val]) => (
            <div key={lbl} style={S.metaItem}>
              <span style={S.metaLabel}>{lbl}</span>
              <span style={S.metaVal}>{val}</span>
            </div>
          ))}
        </div>
        <div style={S.topBtnGroup}>
          <button style={S.modifyBtn} onClick={() => setShowModify((v) => !v)}>
            Modify {showModify ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {showModify && (
        <div style={{ maxHeight: 500 }}>
          <ModifyPanel tripType={tripType} setTripType={setTripType} navigate={navigate} searchData={searchData} />
        </div>
      )}

      <div style={S.mainArea} className="td-main">
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          availableAirlines={availableAirlines}
          priceRange={priceRange}
        />
        <div style={S.resultsArea}>
          <div style={S.sortTabs}>
            {["Best", "Cheapest", "Fastest"].map((tab, i) => (
              <div key={tab} onClick={() => setSortTab(tab)} style={{ ...S.sortTab, background: sortTab === tab ? "#F5F9FF" : "#fff", borderRight: i < 2 ? "1px solid #E8EAF0" : "none", boxShadow: sortTab === tab ? "inset 0 -3px 0 #1a73e8" : "none" }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: sortTab === tab ? "#1a73e8" : "#0F1B33", marginBottom: 2 }}>{tab}</div>
                <div style={{ fontSize: 12.5, color: "#6B7385", fontWeight: 600 }}>
                  {typeof tabMeta[tab].price === "number" ? `₹${Math.round(tabMeta[tab].price).toLocaleString("en-IN")}` : tabMeta[tab].price}
                  {" · "}{tabMeta[tab].dur}
                </div>
              </div>
            ))}
          </div>

          <div style={S.navBtns}>
            <button
              style={{ ...S.navBtn, opacity: visibleCount <= ITEMS_PER_PAGE ? 0.5 : 1 }}
              disabled={visibleCount <= ITEMS_PER_PAGE}
              onClick={() => setVisibleCount((v) => Math.max(ITEMS_PER_PAGE, v - ITEMS_PER_PAGE))}
            >
              ◀ Prev 10
            </button>

            <span style={{ padding: "6px 14px", fontSize: 12, color: "#4A5670" }}>
              {listCount} flights · Showing {Math.min(visibleCount, listCount)} · Page {currentPageDisplay} / {totalPages}
            </span>

            <button
              style={{ ...S.navBtn, opacity: !hasMore ? 0.5 : 1 }}
              disabled={!hasMore}
              onClick={() => setVisibleCount((v) => v + ITEMS_PER_PAGE)}
            >
              Next 10 ▶
            </button>

            {/* NEW: View toggle + TDS button (right side) */}
            <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {canToggleView && (
                <button
                  type="button"
                  style={splitView ? S.toggleBtnOn : S.toggleBtn}
                  onClick={() => setUserView(splitView ? "combined" : "split")}
                  title={splitView ? "Onward + Return ek saath dekhne ke liye" : "Onward aur Return alag-alag dekhne ke liye"}
                >
                  {splitView ? "▤ Combined view" : "⇆ Split view"}
                </button>
              )}
              {/* TDS button: ab har search me dikhega (domestic / international, one way / round trip) */}
              {flights.length > 0 && (
                <button
                  type="button"
                  style={showTds ? S.toggleBtnOn : S.toggleBtn}
                  onClick={() => setShowTds((v) => !v)}
                >
                  {showTds ? "Hide TDS" : "TDS"}
                </button>
              )}
            </div>
          </div>

          {!splitView && (
            <div style={S.colHead}>
              <span>Airline</span>
              <span style={{ textAlign: "center" }}>Departure</span>
              <span style={{ textAlign: "center" }}>Duration</span>
              <span style={{ textAlign: "center" }}>Arrival</span>
              <span style={{ textAlign: "right" }}>Fare</span>
            </div>
          )}

          {error && !loading && (
            <div style={{ textAlign: "center", padding: "16px 0", color: "#c2185b", fontSize: 13 }}>{error}</div>
          )}

          {listCount === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#6B7385", fontSize: 14 }}>
              {loading ? "" : "No flights match your filters."}
            </div>
          ) : splitView ? (
            <div style={S.splitContainer} className="split-container">
              {/* ONWARD COLUMN */}
              <div style={S.splitColumn} className="split-column split-column-onward">
                <div style={S.splitColumnHeader}>✈ Onward Flights — tap to select</div>
                <div style={S.splitColumnBody}>
                  {onwardColumnList.slice(0, visibleCount).map((f, i) => (
                    <OnwardLegCard
                      key={`onw-${f.ContractId || i}`}
                      flight={f}
                      index={i}
                      combined={hasCombinedRT}
                      isSelected={selectedOnward?.flight?.ContractId === f.ContractId}
                      onSelect={() => (hasCombinedRT ? handleSelectCombined(f) : handleSelectOnward(f))}
                      showTds={showTds}
                    />
                  ))}
                  {onwardColumnList.length > visibleCount && (
                    <div style={S.loadingMore}>Load more from "Next 10" above</div>
                  )}
                </div>
              </div>

              {/* RETURN COLUMN */}
              <div style={S.splitColumn} className="split-column split-column-return">
                <div style={S.splitColumnHeader}>↩ Return Flights — tap to select</div>
                <div style={S.splitColumnBody}>
                  {returnColumnList.slice(0, visibleCount).map((f, i) => (
                    <ReturnLegCard
                      key={`ret-${f.ContractId || i}`}
                      flight={f}
                      index={i}
                      combined={hasCombinedRT}
                      isSelected={selectedReturn?.flight?.ContractId === f.ContractId}
                      onSelect={() => (hasCombinedRT ? handleSelectCombined(f) : handleSelectReturn(f))}
                      showTds={showTds}
                    />
                  ))}
                  {returnColumnList.length > visibleCount && (
                    <div style={S.loadingMore}>Load more from "Next 10" above</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              {combinedList.map((f, i) => (
                <FlightCard
                  key={f.ContractId || i}
                  flight={f}
                  returnFlight={showPairs ? cheapestReturnMap.get(f.ContractId) : null}
                  index={i}
                  sessionData={sessionData}
                  isDomestic={isDomestic}
                  searchData={searchData}
                  passengersArray={passengersArray}
                  showTds={showTds}
                  allContracts={flights}
                />
              ))}
              {hasMore && (
                <div style={S.loadingMore}>Scroll down to see more .....</div>
              )}
            </>
          )}
        </div>
      </div>

      {bookingBarActive && (
        <BookingBar
          onwardFlight={selectedOnward.flight}
          returnFlight={selectedReturn.flight}
          sessionData={sessionData}
          isDomestic={isDomestic}
          searchData={searchData}
          onClear={clearSelection}
          passengersArray={passengersArray}
          showTds={showTds}
          allContracts={flights}
        />
      )}

      <KudoChat
        flights={showPairs ? pairableOnward : filteredFlights}
        allFlights={flights}
        filters={filters}
        setFilters={setFilters}
        setSortTab={setSortTab}
        priceRange={priceRange}
        searchData={searchData}
        getDisplayData={getDisplayData}
        raise={!!bookingBarActive}
      />
    </div>
  );
}